/* eslint-disable react-hooks/exhaustive-deps */
import { useDispatch, useSelector } from "react-redux";
import CodeableConceptEntry, { DisplayFromFHIRCodeableConcept, displayStringFromFHIRCodeableConcept, simpleDisplayStringFromFHIRCodeableConcept } from "../datatypeComponents/CodeableConceptEntry";
import { displayStringFromFHIRIdentifier } from "../datatypeComponents/IdentifierEntry";
import PeriodEntry, { displayStringFromFHIRPeriod } from "../datatypeComponents/PeriodEntry";
import ReferenceEntry, { DisplayFromFHIRReference } from "../datatypeComponents/ReferenceEntry";
import { selectEditMode } from "../slices/editMode/editModeSlice";
import { resourceUpdated, selectResourceById } from "../slices/resourceCollection/resourceCollectionSlice";
import { useEffect, useState } from "react";
import { changeTopNavigationElements } from "../slices/navigationPanel/navigationPanelSlice";
import NarrativeEntry, { DisplayFromFHIRXhtml } from "../datatypeComponents/NarrativeEntry";
import MarkdownEntry, { DisplayFromFHIRMarkdown } from "../datatypeComponents/MarkdownEntry";
import { DisplayFromFHIRCodeableReference } from "../datatypeComponents/CodeableReferenceEntry";
import { DisplayFromFHIRRelatedArtifact } from "../datatypeComponents/RelatedArtifactEntry";
import { DisplayFromFHIRRelatesTo } from "../datatypeComponents/RelatesToEntry";
import { DisplayFromFHIRAnnotation } from "../datatypeComponents/AnnotationEntry";
import updateElement, { updateNarrativeElement } from "../functions/updateElement";
import Spinner from "../components/Spinner";
import ExpandToAddOrEdit from "../components/ExpandToAddOrEdit";
import SEVCO, { statisticTypeCodeableConceptSet, studyDesignCodeableConceptSet } from "../dictionaries/SEVCO";
import ArrayEntry from "../datatypeComponents/ArrayEntry";
import UnsignedIntEntry from "../datatypeComponents/UnsignedIntEntry";
import StringEntry from "../datatypeComponents/StringEntry";
import BooleanEntry from "../datatypeComponents/BooleanEntry";
import { changeBrowserTabTitle } from "../slices/browserTabTitle/browserTabTitleSlice";
import { changeTool } from "../slices/footer/footerSlice";
import generateNarrativeForResearchStudyResource from "./generateNarrativeFunctions/generateNarrativeForResearchStudyResource";
import { selectOlderVersionFhirString, selectViewingOlderVersion } from "../slices/viewingOlderVersion/viewingOlderVersionSlice";
import { languageCodeDisplay, languageCodeOptions, languageCodes, titleTypeLookup } from "../dictionaries/CodeSystemLookup";
import CodeEntry from "../datatypeComponents/CodeEntry";
import { Button, Dropdown, Modal, Table } from "semantic-ui-react";
import generateM11Section3Composition from "./m11ReportFunctions/m11Section3Generator";
import submitFhirJson from "../functions/submitFhirJsonAPI";
import { selectCurrentUserApiToken, selectSseConnectionId } from "../slices/currentUser/currentUserSlice";
import getIdFromReference from "../functions/getIdFromReference";
import getFhirJson from "../functions/getFhirJsonAPI";
import generateM11ReportComposition from "./m11ReportFunctions/m11ReportGenerator";
import jsonEquals from "../functions/jsonEquals";
import { useNavigate } from "react-router-dom";
import submitResource from "./resourceDisplayCommonFunctions/submitResource";
import { convertValueSetToCodeableConceptSet, generateValueSetLookup, generateValueSetOptions } from "../dictionaries/THOandFHIRValueSets";
import { TextField } from "@mui/material";
import { researchStudyDotPhaseValueSet, researchStudyPhaseCodeableConceptSet } from "./editReferencedResourceComponents/ResearchStudyResourceEdit";
import ReferenceEntryForEstimand from "../datatypeComponents/ReferenceEntryForEstimand";
import PositiveIntEntry from "../datatypeComponents/PositiveIntEntry";
import { submitComparativeEvidenceReportFromResearchStudy } from "../functions/submitNewComparativeAPI";
import { changeFoiFocus } from "../slices/foiOfFocus/foiOfFocusSlice";

const blindedRoleValueSet = [
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C142710",
        "display": "Participant"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C17445",
        "display": "Care Provider"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C25936",
        "display": "Investigator"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C207599",
        "display": "Outcomes Assessor"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C51877",
        "display": "Statistician"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C70793",
        "display": "Sponsor"
    },
    {
        "system": "http://ncicb.nci.nih.gov/xml/owl/EVS/Thesaurus.owl",
        "code": "C48660",
        "display": "Not Applicable"
    }
];
const blindedRoleCodeableConceptSet = blindedRoleValueSet.map(coding => {
    return { "coding": [coding] };
});

const getResourceDictionaryForEstimands = async (researchStudyJson, apiToken) => {
    let resourceDictionary = {};
    if (researchStudyJson?.objective?.length) {
        for (const objective of researchStudyJson.objective) {
            if (objective?.outcomeMeasure?.length) {
                for (const outcome of objective.outcomeMeasure) {
                    if (outcome.endpoint) {
                        let theFoi = getIdFromReference(outcome.endpoint);
                        if (!isNaN(theFoi)) {
                            let theResource = await getFhirJson(theFoi, null, apiToken);
                            if (theResource) {
                                resourceDictionary[theFoi] = theResource;
                            }
                        }
                    }
                    if (outcome.population) {
                        let theFoi = getIdFromReference(outcome.population);
                        if (!isNaN(theFoi)) {
                            let theResource = await getFhirJson(theFoi, null, apiToken);
                            if (theResource) {
                                resourceDictionary[theFoi] = theResource;
                            }
                        }
                    }
                    if (outcome.intervention) {
                        let theFoi = getIdFromReference(outcome.intervention);
                        if (!isNaN(theFoi)) {
                            let theResource = await getFhirJson(theFoi, null, apiToken);
                            if (theResource) {
                                resourceDictionary[theFoi] = theResource;
                            }
                        }
                    }
                    if (outcome.comparator) {
                        let theFoi = getIdFromReference(outcome.comparator);
                        if (!isNaN(theFoi)) {
                            let theResource = await getFhirJson(theFoi, null, apiToken);
                            if (theResource) {
                                resourceDictionary[theFoi] = theResource;
                            }
                        }
                    }
                }
            }
        }
    }
    return resourceDictionary;
}

const createM11Section3Composition = async (researchStudyResourceState, apiToken, setExtensionState) => {
    let resourceDictionary = await getResourceDictionaryForEstimands(researchStudyResourceState, apiToken);
    let compositionJson = generateM11Section3Composition(researchStudyResourceState, resourceDictionary);
    if (compositionJson?.title) {
        let response = await submitFhirJson(compositionJson, compositionJson.title, apiToken, "active");
        if (response?.success && response.formstateid) {
            let newFOI = response.formstateid;
            let newReference = {
                "reference": "Composition/" + newFOI,
                "type": "Composition",
                "display": compositionJson.title
            };

            setExtensionState(prevState => {
                return { ...prevState, "section3Narrative": newReference };
            });
        }
    } else {
        alert("Unable to create M11ReportSection03 Composition.");
    }
}

const getResourceDictionaryForM11Report = async (researchStudyJson, apiToken, extensionState) => {
    let resourceDictionary = {};
    if (extensionState?.section3Narrative) {
        let theFoi = getIdFromReference(extensionState.section3Narrative);
        if (!isNaN(theFoi)) {
            let theResource = await getFhirJson(theFoi, null, apiToken);
            if (theResource) {
                resourceDictionary[theFoi] = theResource;
            }
        }
    }
    if (researchStudyJson?.recruitment?.eligibility) {
        let theFoi = getIdFromReference(researchStudyJson.recruitment.eligibility);
        if (!isNaN(theFoi)) {
            let theResource = await getFhirJson(theFoi, null, apiToken);
            if (theResource) {
                resourceDictionary[theFoi] = theResource;
            }
        }
    }
    return resourceDictionary;
}

const createComparativeEvidenceReport = async (createComparativeEvidenceReportModalState, apiToken, navigate, dispatch) => {

    let response = await submitComparativeEvidenceReportFromResearchStudy(apiToken, createComparativeEvidenceReportModalState);
    if (response) {
        if (response.success && response.compositionFOI) {
            if (response.message) {
                alert("A ComparativeEvidenceReport was created but ..." + response.message);
            }
            navigate("/resources/Composition/" + response.compositionFOI);
            dispatch(changeFoiFocus(response.compositionFOI.toString()));
        } else {
            if (response.message) {
                alert(response.message);
            } else {
                alert("Failed response from server.");
            }
        }
    } else {
        alert("No response from server.");
    }
}

const CreateComparativeEvidenceReportModal = ({ createComparativeEvidenceReportModalState,
    setCreateComparativeEvidenceReportModalState, researchStudyResourceState,
    containedResourceDictionary }) => {

    const dispatch = useDispatch();
    const connectionId = useSelector(selectSseConnectionId);
    const apiToken = useSelector(selectCurrentUserApiToken);
    const navigate = useNavigate();

    useEffect(() => {
        if (researchStudyResourceState.title && researchStudyResourceState.id) {
            let newModalStateDetail = {
                modalOpen: true,
                title: "Results Report for " + researchStudyResourceState.title,
                researchStudyReference: {
                    "reference": "ResearchStudy/" + researchStudyResourceState.id,
                    "type": "ResearchStudy",
                    "display": researchStudyResourceState.title
                },
                eligibilityCriteriaReference: "",
                studyGroupReference: "",
                numberEnrolled: NaN,
                interventionDescription: "",
                interventionGroupReference: "",
                numberIntervention: NaN,
                comparatorDescription: "",
                comparatorGroupReference: "",
                numberComparator: NaN,
                comparisonGroupDictionary: {}
            };
            if (researchStudyResourceState.recruitment) {
                if (researchStudyResourceState.recruitment.eligibility) {
                    newModalStateDetail.eligibilityCriteriaReference = researchStudyResourceState.recruitment.eligibility;
                }
                if (researchStudyResourceState.recruitment.actualGroup) {
                    newModalStateDetail.studyGroupReference = researchStudyResourceState.recruitment.actualGroup;
                }
                if (researchStudyResourceState.recruitment.actualNumber) {
                    newModalStateDetail.numberEnrolled = researchStudyResourceState.recruitment.actualNumber;
                }
            }
            if (researchStudyResourceState.comparisonGroup?.length) {
                let comparisonGroupDictionary = {};
                for (const index in researchStudyResourceState.comparisonGroup) {
                    let comparisonGroup = researchStudyResourceState.comparisonGroup[index];
                    let name = comparisonGroup.name || comparisonGroup.observedGroup?.display || comparisonGroup.eligibility?.display || "Unnamed Group " + index;
                    comparisonGroupDictionary[name] = comparisonGroup;
                }
                newModalStateDetail.comparisonGroupDictionary = comparisonGroupDictionary;
            }
            setCreateComparativeEvidenceReportModalState(newModalStateDetail);
        }
    }, [researchStudyResourceState]);

    useEffect(() => {
        if (createComparativeEvidenceReportModalState.selectedIntervention) {
            let interventionDescription = "";
            let interventionGroupReference = "";
            let numberIntervention = NaN;
            if (createComparativeEvidenceReportModalState.selectedIntervention.eligibility) {
                interventionDescription = createComparativeEvidenceReportModalState.selectedIntervention.eligibility;
            } else {
                interventionDescription = { "display": createComparativeEvidenceReportModalState.selectedIntervention.name || "Intervention" };
                if (createComparativeEvidenceReportModalState.selectedIntervention.description) {
                    interventionDescription.display += ": " + createComparativeEvidenceReportModalState.selectedIntervention.description;
                }
            }
            if (createComparativeEvidenceReportModalState.selectedIntervention.observedGroup) {
                interventionGroupReference = createComparativeEvidenceReportModalState.selectedIntervention.observedGroup;
            }
            if (createComparativeEvidenceReportModalState.selectedIntervention.actualNumber) {
                numberIntervention = createComparativeEvidenceReportModalState.selectedIntervention.actualNumber;
            }
            setCreateComparativeEvidenceReportModalState(prevState => {
                return {
                    ...prevState,
                    interventionDescription: interventionDescription,
                    interventionGroupReference: interventionGroupReference,
                    numberIntervention: numberIntervention
                }
            })
        }
    }, [createComparativeEvidenceReportModalState.selectedIntervention]);

    useEffect(() => {
        if (createComparativeEvidenceReportModalState.selectedComparator) {
            let comparatorDescription = "";
            let comparatorGroupReference = "";
            let numberComparator = NaN;
            if (createComparativeEvidenceReportModalState.selectedComparator.eligibility) {
                comparatorDescription = createComparativeEvidenceReportModalState.selectedComparator.eligibility;
            } else {
                comparatorDescription = { "display": createComparativeEvidenceReportModalState.selectedComparator.name || "Comparator" };
                if (createComparativeEvidenceReportModalState.selectedComparator.description) {
                    comparatorDescription.display += ": " + createComparativeEvidenceReportModalState.selectedComparator.description;
                }
            }
            if (createComparativeEvidenceReportModalState.selectedComparator.observedGroup) {
                comparatorGroupReference = createComparativeEvidenceReportModalState.selectedComparator.observedGroup;
            }
            if (createComparativeEvidenceReportModalState.selectedComparator.actualNumber) {
                numberComparator = createComparativeEvidenceReportModalState.selectedComparator.actualNumber;
            }
            setCreateComparativeEvidenceReportModalState(prevState => {
                return {
                    ...prevState,
                    comparatorDescription: comparatorDescription,
                    comparatorGroupReference: comparatorGroupReference,
                    numberComparator: numberComparator
                }
            })
        }
    }, [createComparativeEvidenceReportModalState.selectedComparator]);

    let modalContent = <div style={{
        paddingTop: "0px", paddingLeft: "20px", paddingRight: "20px",
        paddingBottom: "40px", width: "100%", height: "100%", overflow: "auto"
    }}>
        <span style={{
            position: "absolute", backgroundColor: "#FFFFFF", width: "calc(90vw - 38px)", zIndex: 100,
            paddingTop: "6px", paddingBottom: "6px"
        }} >
            <Button style={{
                color: "#FFFFFF",
                width: "230px", float: "left"
            }}
                className="formButton" disabled={!createComparativeEvidenceReportModalState.comparatorDescription}
                content="Submit" positive={true}
                onClick={() => {
                    createComparativeEvidenceReport(createComparativeEvidenceReportModalState, apiToken, navigate, dispatch);
                }}
            />
            &nbsp;&nbsp;&nbsp;
            <Button style={{ color: "#000000", width: "180px", float: "right" }}
                className="formButton"
                content="Close Window"
                onClick={() => {
                    setCreateComparativeEvidenceReportModalState(prevState => { return { ...prevState, modalOpen: false }; });
                }}
            />
        </span>
        <br /><br /><br />
        <div className='tableFixHead'>
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Edit the </span>title<span> for the </span>Comparative Evidence Report<span>.</span>
            </div>
            <StringEntry elementName='title' fieldLabel='Title'
                startingValue={createComparativeEvidenceReportModalState.title}
                setResourceState={setCreateComparativeEvidenceReportModalState} />
            <h4>Revise the Study Group details as needed.</h4>
            <div style={{ marginLeft: "24px" }}>
                <Table>
                    <Table.Body>
                        <Table.Row>
                            <Table.Cell>
                                <div>
                                    <p style={{ marginTop: "0px" }}>Specify the <b>eligibility criteria</b> for the <b>research study</b>.</p>
                                    <ReferenceEntry elementName='eligibilityCriteriaReference'
                                        fieldLabel='Eligibility Criteria'
                                        containedResourceDictionary={containedResourceDictionary}
                                        startingValue={createComparativeEvidenceReportModalState.eligibilityCriteriaReference}
                                        startingResourceType="Group" doNotShowFieldLabel
                                        referencedResourceTypes={["Group"]}
                                        enableCreation={true} startCollapsed={true}
                                        setProfile={"StudyEligibilityCriteria"}
                                        setResourceState={setCreateComparativeEvidenceReportModalState} />
                                </div>
                            </Table.Cell>
                            <Table.Cell>
                                <div style={{ marginBottom: "0px" }}>
                                    <p>Specify the <b>sample size</b> for the <b>enrollment group</b>.</p>
                                    <PositiveIntEntry elementName='numberEnrolled' fieldLabel='Number Enrolled'
                                        startingValue={createComparativeEvidenceReportModalState.numberEnrolled}
                                        setResourceState={setCreateComparativeEvidenceReportModalState} />
                                </div>
                            </Table.Cell>
                        </Table.Row>
                    </Table.Body>
                </Table>
            </div>
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Select the </span>intervention group<span> and the </span>comparison group<span>.</span>
            </div>
            <div style={{ marginLeft: "24px" }}>
                {(createComparativeEvidenceReportModalState.comparisonGroupDictionary &&
                    Object.keys(createComparativeEvidenceReportModalState.comparisonGroupDictionary).length > 0)
                    ?
                    <>{Object.keys(createComparativeEvidenceReportModalState.comparisonGroupDictionary).map((name, index) => {
                        return <div key={index}>
                            <br />
                            <p><b>{name}</b> {createComparativeEvidenceReportModalState.comparisonGroupDictionary[name].description}</p>
                            <div style={{ marginLeft: "24px" }}>
                                <Button style={{ color: "#000000", width: "240px" }}
                                    className="formButton"
                                    content="Set as intervention"
                                    onClick={() => {
                                        setCreateComparativeEvidenceReportModalState(prevState => {
                                            return {
                                                ...prevState,
                                                "selectedIntervention": createComparativeEvidenceReportModalState.comparisonGroupDictionary[name]
                                            }
                                        })
                                    }}
                                />
                                &nbsp;&nbsp;&nbsp;
                                <Button style={{ color: "#000000", width: "240px" }}
                                    className="formButton"
                                    content="Set as comparator"
                                    onClick={() => {
                                        setCreateComparativeEvidenceReportModalState(prevState => {
                                            return {
                                                ...prevState,
                                                "selectedComparator": createComparativeEvidenceReportModalState.comparisonGroupDictionary[name]
                                            }
                                        })
                                    }}
                                />
                            </div>
                        </div>
                    })}</>
                    :
                    <p>No comparison groups specified in the research study.</p>}
            </div>
        </div>
        <div style={{ marginTop: "16px", marginLeft: "24px" }}>
            <Table>
                <Table.Body>
                    <Table.Row>
                        <Table.Cell style={{ verticalAlign: "top" }}>
                            <div className="h4Style h4EntryLabel" style={{ marginTop: "0px", marginBottom: "0px" }}>
                                <span>Specify the </span>intervention description<span> for the </span>research study<span>.</span>
                            </div>
                            <ReferenceEntry elementName='interventionDescription' fieldLabel='Intervention Description'
                                containedResourceDictionary={containedResourceDictionary}
                                startingValue={createComparativeEvidenceReportModalState.interventionDescription}
                                startingResourceType="Group"
                                referencedResourceTypes={["Group"]}
                                enableCreation={true} startCollapsed={true}
                                setProfile={"ConceptualExposureDefinition"}
                                setResourceState={setCreateComparativeEvidenceReportModalState} />
                        </Table.Cell>
                        <Table.Cell style={{ verticalAlign: "top" }}>
                            <div className="h4Style h4EntryLabel" style={{ marginTop: "0px", marginBottom: "0px" }}>
                                <span>Specify the </span>sample size<span> for the </span>intervention group<span>.</span>
                            </div>
                            <PositiveIntEntry elementName='numberIntervention' fieldLabel='Number in Intervention Group'
                                startingValue={createComparativeEvidenceReportModalState.numberIntervention}
                                setResourceState={setCreateComparativeEvidenceReportModalState} />
                        </Table.Cell>
                    </Table.Row>
                </Table.Body>
            </Table>
        </div>

        <div style={{ marginTop: "16px", marginLeft: "24px" }}>
            <Table>
                <Table.Body>
                    <Table.Row>
                        <Table.Cell style={{ verticalAlign: "top" }}>
                            <div className="h4Style h4EntryLabel" style={{ marginTop: "0px", marginBottom: "0px" }}>
                                <span>Specify the </span>comparator description<span> for the </span>research study<span>.</span>
                                <ReferenceEntry elementName='comparatorDescription' fieldLabel='Comparator Description'
                                    containedResourceDictionary={containedResourceDictionary}
                                    startingValue={createComparativeEvidenceReportModalState.comparatorDescription}
                                    startingResourceType="Group"
                                    referencedResourceTypes={["Group"]}
                                    enableCreation={true} startCollapsed={true}
                                    setProfile={"ConceptualComparatorDefinition"}
                                    setResourceState={setCreateComparativeEvidenceReportModalState} />
                            </div>
                        </Table.Cell>
                        <Table.Cell style={{ verticalAlign: "top" }}>
                            <div className="h4Style h4EntryLabel" style={{ marginTop: "0px", marginBottom: "0px" }}>
                                <span>Specify the </span>sample size<span> for the </span>comparator group<span>.</span>
                            </div>
                            <PositiveIntEntry elementName='numberComparator' fieldLabel='Number in Comparator Group'
                                startingValue={createComparativeEvidenceReportModalState.numberComparator}
                                setResourceState={setCreateComparativeEvidenceReportModalState} />
                        </Table.Cell>
                    </Table.Row>
                </Table.Body>
            </Table>
        </div>
        <br />
    </div>;

    return <>
        <Modal
            style={{ padding: "0px", margin: "0px" }}
            dimmer={<Modal.Dimmer style={{ backgroundColor: "#00000077" }} />}
            open={createComparativeEvidenceReportModalState?.modalOpen}
            centered={false}
            content={modalContent}
            className="viewmywork"
        />
    </>
}

const CompleteObservedComparisonGroupsModal = ({ completeObservedComparisonGroupsModalState,
    setCompleteObservedComparisonGroupsModalState, researchStudyResourceState, setResearchStudyResourceState,
    outcomeMeasuresReportContained }) => {

    const outcomeMeasuresReportContainedDictionary = {};
    const outcomeMeasuresReportContainedCodeList = [];
    for (const resource of outcomeMeasuresReportContained) {
        if (resource.resourceType === "Group") {
            let thisKey = resource.id + ": " + resource.title;
            outcomeMeasuresReportContainedCodeList.push(thisKey);
            outcomeMeasuresReportContainedDictionary[thisKey] = resource;
        }
    }

    const [comparisonGroupDictionaryState, setComparisonGroupDictionaryState] = useState({});
    const [startingComparisonGroupDictionaryState, setStartingComparisonGroupDictionaryState] = useState({});
    const [assignToComparisonGroupState, setAssignToComparisonGroupState] = useState({});

    useEffect(() => {
        if (researchStudyResourceState.comparisonGroup?.length) {
            let comparisonGroupDictionary = {};
            let assignToComparisonGroupDictionary = {};
            for (const index in researchStudyResourceState.comparisonGroup) {
                let comparisonGroup = researchStudyResourceState.comparisonGroup[index];
                let name = comparisonGroup.name || comparisonGroup.observedGroup?.display || comparisonGroup.eligibility?.display || "Unnamed Group " + index;
                comparisonGroupDictionary[name] = comparisonGroup;
                assignToComparisonGroupDictionary[name] = null;
            }
            setComparisonGroupDictionaryState(comparisonGroupDictionary);
            setStartingComparisonGroupDictionaryState(comparisonGroupDictionary);
            setAssignToComparisonGroupState(assignToComparisonGroupDictionary);
        }
    }, [researchStudyResourceState]);

    useEffect(() => {
        if (Object.keys(assignToComparisonGroupState).length > 0) {
            for (const key in assignToComparisonGroupState) {
                if (assignToComparisonGroupState[key]) {
                    let longkey = assignToComparisonGroupState[key];
                    let observedGroupReference = {
                        "reference": "#" + outcomeMeasuresReportContainedDictionary[longkey].id,
                        "type": "Group",
                        "display": outcomeMeasuresReportContainedDictionary[longkey].title
                    }
                    setComparisonGroupDictionaryState(prevState => {
                        return {
                            ...prevState,
                            [key]: {
                                ...prevState[key],
                                "observedGroup": observedGroupReference
                            }
                        }
                    });
                }
            }
        }
    }, [assignToComparisonGroupState]);

    const updateComparisonGroupArray = () => {
        let comparisonGroupArray = [];
        for (const key in comparisonGroupDictionaryState) {
            comparisonGroupArray.push(comparisonGroupDictionaryState[key]);
        }
        setResearchStudyResourceState(prevState => {
            return {
                ...prevState,
                comparisonGroup: comparisonGroupArray,
                contained: prevState["contained"].concat(outcomeMeasuresReportContained)
            };
        });
        alert("Your changes have been applied. Click Save All Changes in the left navigation panel to see the effects.");
        setCompleteObservedComparisonGroupsModalState(prevState => { return { ...prevState, modalOpen: false }; });
    }

    let modalContent = <div style={{
        paddingTop: "0px", paddingLeft: "20px", paddingRight: "20px",
        paddingBottom: "40px", width: "100%", height: "100%", overflow: "auto"
    }}>
        <span style={{
            position: "absolute", backgroundColor: "#FFFFFF", width: "calc(90vw - 38px)", zIndex: 100,
            paddingTop: "6px", paddingBottom: "6px"
        }} >
            <Button style={{
                color: "#FFFFFF",
                width: "230px", float: "left"
            }}
                className="formButton"
                disabled={Object.keys(comparisonGroupDictionaryState).length === 0
                    || jsonEquals(comparisonGroupDictionaryState, startingComparisonGroupDictionaryState)}
                content="Submit" positive={true}
                onClick={() => {
                    updateComparisonGroupArray();
                }}
            />
            &nbsp;&nbsp;&nbsp;
            <Button style={{ color: "#000000", width: "180px", float: "right" }}
                className="formButton"
                content="Close Window"
                onClick={() => {
                    setCompleteObservedComparisonGroupsModalState(prevState => { return { ...prevState, modalOpen: false }; });
                }}
            />
        </span>
        <br /><br /><br />
        <div className='tableFixHead'>
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>For each </span>comparison group<span>, select the </span>observed group<span> from the Outcome Measures Report.</span>
            </div>
            <div style={{ marginLeft: "24px" }}>
                {Object.keys(comparisonGroupDictionaryState).length > 0
                    ?
                    <>{Object.keys(comparisonGroupDictionaryState).map((name, index) => {
                        return <div key={index}>
                            <br />
                            <p><b>{name}</b> {comparisonGroupDictionaryState[name].description}</p>
                            <CodeEntry elementName={name} fieldLabel="Select Observed Group"
                                startingValue={assignToComparisonGroupState[name]}
                                setResourceState={setAssignToComparisonGroupState}
                                allowedValues={outcomeMeasuresReportContainedCodeList} />
                        </div>
                    })}</>
                    :
                    <p>No comparison groups specified in the research study.</p>
                }
            </div>
        </div>
    </div>;

    return <>
        <Modal
            style={{ padding: "0px", margin: "0px" }}
            dimmer={<Modal.Dimmer style={{ backgroundColor: "#00000077" }} />}
            open={completeObservedComparisonGroupsModalState?.modalOpen}
            centered={false}
            content={modalContent}
            className="viewmywork"
        />
    </>
}

const createM11ReportComposition = async (researchStudyJson, apiToken, extensionState, setResearchStudyResourceState) => {
    let resourceDictionary = await getResourceDictionaryForM11Report(researchStudyJson, apiToken, extensionState,);
    let m11ReportJsonDictionary = generateM11ReportComposition(researchStudyJson, resourceDictionary, extensionState);
    let response01 = await submitFhirJson(m11ReportJsonDictionary.m11ReportSection01Json, m11ReportJsonDictionary.m11ReportSection01Json.title, apiToken, "active");
    let response02 = await submitFhirJson(m11ReportJsonDictionary.m11ReportSection02Json, m11ReportJsonDictionary.m11ReportSection02Json.title, apiToken, "active");
    let response03 = await submitFhirJson(m11ReportJsonDictionary.m11ReportSection03Json, m11ReportJsonDictionary.m11ReportSection03Json.title, apiToken, "active");
    let response04 = await submitFhirJson(m11ReportJsonDictionary.m11ReportSection04Json, m11ReportJsonDictionary.m11ReportSection04Json.title, apiToken, "active");
    let response05 = await submitFhirJson(m11ReportJsonDictionary.m11ReportSection05Json, m11ReportJsonDictionary.m11ReportSection05Json.title, apiToken, "active");
    let response06 = await submitFhirJson(m11ReportJsonDictionary.m11ReportSection06Json, m11ReportJsonDictionary.m11ReportSection06Json.title, apiToken, "active");
    let response07 = await submitFhirJson(m11ReportJsonDictionary.m11ReportSection07Json, m11ReportJsonDictionary.m11ReportSection07Json.title, apiToken, "active");
    let response08 = await submitFhirJson(m11ReportJsonDictionary.m11ReportSection08Json, m11ReportJsonDictionary.m11ReportSection08Json.title, apiToken, "active");
    let response09 = await submitFhirJson(m11ReportJsonDictionary.m11ReportSection09Json, m11ReportJsonDictionary.m11ReportSection09Json.title, apiToken, "active");
    let response10 = await submitFhirJson(m11ReportJsonDictionary.m11ReportSection10Json, m11ReportJsonDictionary.m11ReportSection10Json.title, apiToken, "active");
    let response11 = await submitFhirJson(m11ReportJsonDictionary.m11ReportSection11Json, m11ReportJsonDictionary.m11ReportSection11Json.title, apiToken, "active");
    let response12 = await submitFhirJson(m11ReportJsonDictionary.m11ReportSection12Json, m11ReportJsonDictionary.m11ReportSection12Json.title, apiToken, "active");
    let response13 = await submitFhirJson(m11ReportJsonDictionary.m11ReportSection13Json, m11ReportJsonDictionary.m11ReportSection13Json.title, apiToken, "active");
    let response14 = await submitFhirJson(m11ReportJsonDictionary.m11ReportSection14Json, m11ReportJsonDictionary.m11ReportSection14Json.title, apiToken, "active");
    let m11ReportJson = m11ReportJsonDictionary.m11ReportContainerJson;
    if (response01.success && response01.formstateid) {
        let m11sectionFoi = response01.formstateid;
        let m11sectionReference = {
            "reference": "Composition/" + m11sectionFoi,
            "type": "Composition",
            "display": m11ReportJsonDictionary.m11ReportSection01Json.title
        };
        m11ReportJson.section[1]["entry"] = [m11sectionReference];
    }
    if (response02.success && response02.formstateid) {
        let m11sectionFoi = response02.formstateid;
        let m11sectionReference = {
            "reference": "Composition/" + m11sectionFoi,
            "type": "Composition",
            "display": m11ReportJsonDictionary.m11ReportSection02Json.title
        };
        m11ReportJson.section[2]["entry"] = [m11sectionReference];
    }
    if (response03.success && response03.formstateid) {
        let m11sectionFoi = response03.formstateid;
        let m11sectionReference = {
            "reference": "Composition/" + m11sectionFoi,
            "type": "Composition",
            "display": m11ReportJsonDictionary.m11ReportSection03Json.title
        };
        m11ReportJson.section[3]["entry"] = [m11sectionReference];
    }
    if (response04.success && response04.formstateid) {
        let m11sectionFoi = response04.formstateid;
        let m11sectionReference = {
            "reference": "Composition/" + m11sectionFoi,
            "type": "Composition",
            "display": m11ReportJsonDictionary.m11ReportSection04Json.title
        };
        m11ReportJson.section[4]["entry"] = [m11sectionReference];
    }
    if (response05.success && response05.formstateid) {
        let m11sectionFoi = response05.formstateid;
        let m11sectionReference = {
            "reference": "Composition/" + m11sectionFoi,
            "type": "Composition",
            "display": m11ReportJsonDictionary.m11ReportSection05Json.title
        };
        m11ReportJson.section[5]["entry"] = [m11sectionReference];
    }
    if (response06.success && response06.formstateid) {
        let m11sectionFoi = response06.formstateid;
        let m11sectionReference = {
            "reference": "Composition/" + m11sectionFoi,
            "type": "Composition",
            "display": m11ReportJsonDictionary.m11ReportSection06Json.title
        };
        m11ReportJson.section[6]["entry"] = [m11sectionReference];
    }
    if (response07.success && response07.formstateid) {
        let m11sectionFoi = response07.formstateid;
        let m11sectionReference = {
            "reference": "Composition/" + m11sectionFoi,
            "type": "Composition",
            "display": m11ReportJsonDictionary.m11ReportSection07Json.title
        };
        m11ReportJson.section[7]["entry"] = [m11sectionReference];
    }
    if (response08.success && response08.formstateid) {
        let m11sectionFoi = response08.formstateid;
        let m11sectionReference = {
            "reference": "Composition/" + m11sectionFoi,
            "type": "Composition",
            "display": m11ReportJsonDictionary.m11ReportSection08Json.title
        };
        m11ReportJson.section[8]["entry"] = [m11sectionReference];
    }
    if (response09.success && response09.formstateid) {
        let m11sectionFoi = response09.formstateid;
        let m11sectionReference = {
            "reference": "Composition/" + m11sectionFoi,
            "type": "Composition",
            "display": m11ReportJsonDictionary.m11ReportSection09Json.title
        };
        m11ReportJson.section[9]["entry"] = [m11sectionReference];
    }
    if (response10.success && response10.formstateid) {
        let m11sectionFoi = response10.formstateid;
        let m11sectionReference = {
            "reference": "Composition/" + m11sectionFoi,
            "type": "Composition",
            "display": m11ReportJsonDictionary.m11ReportSection10Json.title
        };
        m11ReportJson.section[10]["entry"] = [m11sectionReference];
    }
    if (response11.success && response11.formstateid) {
        let m11sectionFoi = response11.formstateid;
        let m11sectionReference = {
            "reference": "Composition/" + m11sectionFoi,
            "type": "Composition",
            "display": m11ReportJsonDictionary.m11ReportSection11Json.title
        };
        m11ReportJson.section[11]["entry"] = [m11sectionReference];
    }
    if (response12.success && response12.formstateid) {
        let m11sectionFoi = response12.formstateid;
        let m11sectionReference = {
            "reference": "Composition/" + m11sectionFoi,
            "type": "Composition",
            "display": m11ReportJsonDictionary.m11ReportSection12Json.title
        };
        m11ReportJson.section[12]["entry"] = [m11sectionReference];
    }
    if (response13.success && response13.formstateid) {
        let m11sectionFoi = response13.formstateid;
        let m11sectionReference = {
            "reference": "Composition/" + m11sectionFoi,
            "type": "Composition",
            "display": m11ReportJsonDictionary.m11ReportSection13Json.title
        };
        m11ReportJson.section[13]["entry"] = [m11sectionReference];
    }
    if (response14.success && response14.formstateid) {
        let m11sectionFoi = response14.formstateid;
        let m11sectionReference = {
            "reference": "Composition/" + m11sectionFoi,
            "type": "Composition",
            "display": m11ReportJsonDictionary.m11ReportSection14Json.title
        };
        m11ReportJson.section[14]["entry"] = [m11sectionReference];
    }
    let response = await submitFhirJson(m11ReportJson, m11ReportJson.title, apiToken, "active");
    if (response?.success && response.formstateid) {
        let newFOI = response.formstateid;
        let newReference = {
            "reference": "Composition/" + newFOI,
            "type": "Composition",
            "display": m11ReportJson.title
        };
        let newRelatesToInstance = {
            "type": {
                "coding": [{
                    "system": "http://terminology.hl7.org/CodeSystem/artifact-relationship-type",
                    "code": "supported-with",
                    "display": "Supported With"
                }],
                "text": "M11 Report"
            },
            "targetReference": newReference
        };

        setResearchStudyResourceState(prevState => {
            let newRelatesTo = [];
            if (prevState.relatesTo) {
                newRelatesTo = JSON.parse(JSON.stringify(prevState.relatesTo));
            }
            newRelatesTo.push(newRelatesToInstance);
            return {
                ...prevState,
                "relatesTo": newRelatesTo
            };
        });
        alert("M11 Report Resources created. Save All Changes then find the link to M11 Report in the Protocol or Metadata section.");
    } else {
        alert("Something did not work right.");
    }
}

const createM11ResearchStudy = (researchStudyResourceState, apiToken, navigate, dispatch) => {
    let newResourceJson = JSON.parse(JSON.stringify(researchStudyResourceState));
    if (!newResourceJson.meta) {
        newResourceJson.meta = {};
    }
    if (!newResourceJson.meta.profile) {
        newResourceJson.meta.profile = [];
    }
    newResourceJson.meta.profile.push("http://hl7.org/fhir/uv/clinical-study-protocol/StructureDefinition/m11-research-study-profile");
    let originalTitle = newResourceJson.title || "FOI " + newResourceJson.id;
    if (newResourceJson.title) {
        newResourceJson.title += " - M11ResearchStudy";
    } else if (newResourceJson.id) {
        newResourceJson.title = "FOI " + newResourceJson.id + " - M11ResearchStudy";
    } else {
        newResourceJson.title = "Unnamed M11ResearchStudy";
    }
    if (newResourceJson.author) {
        delete newResourceJson.author;
    }
    let identifiersToKeep = [];
    if (newResourceJson.identifier) {
        for (let identifierIndex in newResourceJson.identifier) {
            if (newResourceJson.identifier[identifierIndex].system !== "https://fevir.net/FOI") {
                identifiersToKeep.push(newResourceJson.identifier[identifierIndex]);
            }
        }
        delete newResourceJson.identifier;
        if (identifiersToKeep.length > 0) {
            newResourceJson.identifier = identifiersToKeep;
        }
    }
    let relatesToEntry = {
        "type": "derived-from",
        "targetReference": {
            "reference": "ResearchStudy/" + newResourceJson.id,
            "type": "ResearchStudy",
            "display": originalTitle
        }
    }
    if (!newResourceJson.relatesTo) {
        newResourceJson.relatesTo = [];
    }
    newResourceJson.relatesTo.push(relatesToEntry);
    let fhirEntry = JSON.stringify(newResourceJson);
    submitResource(fhirEntry, apiToken, navigate, dispatch, "active");
};

const focusReferenceResourceTypes = ['Medication', 'MedicinalProductDefinition', 'SubstanceDefinition', 'EvidenceVariable'];
const researchStudyDotPurposeTypeValueSet = [
    { system: "http://terminology.hl7.org/CodeSystem/research-study-purpose-type", code: "treatment", display: "Treatment Study" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-purpose-type", code: "prevention", display: "Prevention Study" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-purpose-type", code: "diagnostic", display: "Diagnostic Study" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-purpose-type", code: "screening", display: "Screening Study" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-purpose-type", code: "supportive-care", display: "Supportive Care Study" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-purpose-type", code: "health-services-research", display: "Health Services Research" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-purpose-type", code: "basic-science", display: "Basic Science" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-purpose-type", code: "device-feasibility", display: "Device Feasibility Study" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-purpose-type", code: "safety", display: "Safety Study" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-purpose-type", code: "risk-association", display: "Risk Association Study" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-purpose-type", code: "efficacy", display: "Efficacy Study" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-purpose-type", code: "pharmacokinetic", display: "Pharmacokinetic Study" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-purpose-type", code: "pharmacodynamic", display: "Pharmacodynamic Study" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-purpose-type", code: "bioequivalence", display: "Bioequivalence Study" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-purpose-type", code: "dose-response", display: "Dose-Response Study" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-purpose-type", code: "pharmacogenetic", display: "Pharmacogenetic Study" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-purpose-type", code: "pharmacogenomic", display: "Pharmacogenomic Study" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-purpose-type", code: "pharmacoeconomic", display: "Pharmacoeconomic Study" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-purpose-type", code: "incidence", display: "Incidence Study" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-purpose-type", code: "prevalence", display: "Prevalence Study" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-purpose-type", code: "prediction", display: "Prediction Study" }
];
const researchStudyDotPurposeCodeableConceptSet = researchStudyDotPurposeTypeValueSet.map(concept => {
    return {
        "coding": [concept]
    };
});
const researchStudyDotWhyStoppedValueSet = [
    { system: "http://hl7.org/fhir/research-study-reason-stopped", code: "accrual-goal-met", display: "Accrual Goal Met" },
    { system: "http://hl7.org/fhir/research-study-reason-stopped", code: "closed-due-to-toxicity", display: "Closed due to toxicity" },
    { system: "http://hl7.org/fhir/research-study-reason-stopped", code: "closed-due-to-lack-of-study-progress", display: "Closed due to lack of study progress" },
    { system: "http://hl7.org/fhir/research-study-reason-stopped", code: "temporarily-closed-per-study-design", display: "Temporarily closed per study design" }
];
const resultReferenceResourceTypes = ['Citation', 'Composition', 'DocumentReference', 'Evidence', 'DiagnosticReport'];
const siteReferenceResourceTypes = ['Location', 'ResearchStudy', 'Organization'];

const researchStudyDotAssociatedPartyRoleValueSet = [
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "sponsor", display: "Sponsor" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "lead-sponsor", display: "Lead sponsor" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "sponsor-investigator", display: "Sponsor-investigator" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "primary-investigator", display: "Principal investigator" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "collaborator", display: "Collaborator" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "funding-source", display: "Funding source" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "general-contact", display: "General contact" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "recruitment-contact", display: "Recruitment contact" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "sub-investigator", display: "Sub-investigator" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "study-chair", display: "Study chair" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "irb", display: "Institutional Review Board" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-party-role", code: "data-monitoring", display: "Data Monitoring" }
];
const ResearchStudyAssociatedPartyEntry = ({ elementName, fieldLabel, startingValue, setResourceState,
    startCollapsed, containedResourceDictionary = {} }) => {

    const [partyEntryState, setPartyEntryState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingPartyEntry = {
            name: "", role: "", period: [], classifier: [], party: ""
        };
        if (startingValue) {
            if (startingValue.extension) { startingPartyEntry.extension = startingValue.extension; }
            if (startingValue.name) { startingPartyEntry.name = startingValue.name; }
            if (startingValue.role) { startingPartyEntry.role = startingValue.role; }
            if (startingValue.period) { startingPartyEntry.period = startingValue.period; }
            if (startingValue.classifier) { startingPartyEntry.classifier = startingValue.classifier; }
            if (startingValue.party) { startingPartyEntry.party = startingValue.party; }
        }
        setPartyEntryState(JSON.parse(JSON.stringify(startingPartyEntry)));
    }, []);

    useEffect((() => {
        if (Object.keys(partyEntryState).length > 0) {
            let newPartyEntry = {};
            if (partyEntryState.extension) { newPartyEntry.extension = partyEntryState.extension; }
            if (partyEntryState.name) { newPartyEntry.name = partyEntryState.name; }
            if (partyEntryState.role && Object.keys(partyEntryState.role).length > 0) {
                newPartyEntry.role = partyEntryState.role;
            }
            if (Array.isArray(partyEntryState.period) && partyEntryState.period.length > 0) {
                newPartyEntry.period = partyEntryState.period;
            }
            if (Array.isArray(partyEntryState.classifier) && partyEntryState.classifier.length > 0) {
                newPartyEntry.classifier = partyEntryState.classifier;
            }
            if (partyEntryState.party && Object.keys(partyEntryState.party).length > 0) {
                newPartyEntry.party = partyEntryState.party;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newPartyEntry }; });
        }
    }), [partyEntryState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {startingValue.name && <p><b>Name: </b>{startingValue.name}</p>}
                {startingValue.role && <span><b>Role: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.role} /><br /></span>}
                {Array.isArray(startingValue.period) &&
                    startingValue.period.map((period, periodIndex) => {
                        return <div key={periodIndex}><b>Period: </b>{displayStringFromFHIRPeriod(period)}</div>
                    })}
                {Array.isArray(startingValue.classifier) &&
                    startingValue.classifier.map((classifier, classifierIndex) => {
                        return <div key={classifierIndex}><b>Classifier: </b><DisplayFromFHIRCodeableConcept codeableConcept={classifier} /></div>
                    })}
                {startingValue.party && <div><b>Party: </b><DisplayFromFHIRReference reference={startingValue.party}
                    containedResourceJson={startingValue.party.reference &&
                        containedResourceDictionary[startingValue.party.reference]} /><br /></div>}
            </div>}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else if (Object.keys(partyEntryState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <StringEntry elementName='name' fieldLabel='Name'
                    startingValue={partyEntryState.name} setResourceState={setPartyEntryState} />
                <CodeableConceptEntry elementName='role' fieldLabel='Role'
                    valueSet={researchStudyDotAssociatedPartyRoleValueSet} startCollapsed={startCollapsed}
                    startingValue={partyEntryState.role} setResourceState={setPartyEntryState} />
                <ArrayEntry datatype='Period' elementName='period' fieldLabel='Period'
                    startCollapsed={startCollapsed} startEmptyArrayClosed={true}
                    startingValue={partyEntryState.period} setResourceState={setPartyEntryState} />
                <ArrayEntry datatype='CodeableConcept' elementName='classifier' fieldLabel='Classifier'
                    startCollapsed={startCollapsed} startEmptyArrayClosed={true}
                    startingValue={partyEntryState.classifier} setResourceState={setPartyEntryState} />
                <ReferenceEntry elementName='party' fieldLabel='Party (as Reference)'
                    containedResourceDictionary={containedResourceDictionary}
                    referencedResourceTypes={['Practitioner', 'PractitionerRole', 'Organization']}
                    startingValue={partyEntryState.party} setResourceState={setPartyEntryState} />
            </div>
        </>
    }
}

const ResearchStudyComparisonGroupEntry = ({ elementName, fieldLabel, startingValue, setResourceState,
    startCollapsed, containedResourceDictionary = {} }) => {

    const [comparisonGroupEntryState, setComparisonGroupEntryState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingComparisonGroupEntry = {
            name: "", targetNumber: "", actualNumber: "", eligibility: "", observedGroup: "", description: ""
        };
        if (startingValue) {
            if (startingValue.extension) { startingComparisonGroupEntry.extension = startingValue.extension; }
            if (startingValue.name) { startingComparisonGroupEntry.name = startingValue.name; }
            if (startingValue.targetNumber || startingValue.targetNumber === 0) { startingComparisonGroupEntry.targetNumber = startingValue.targetNumber; }
            if (startingValue.actualNumber || startingValue.actualNumber === 0) { startingComparisonGroupEntry.actualNumber = startingValue.actualNumber; }
            if (startingValue.eligibility) { startingComparisonGroupEntry.eligibility = startingValue.eligibility; }
            if (startingValue.observedGroup) { startingComparisonGroupEntry.observedGroup = startingValue.observedGroup; }
            if (startingValue.description) { startingComparisonGroupEntry.description = startingValue.description; }
            if (startingValue.type) {
                if (startingValue.description) {
                    startingComparisonGroupEntry.description = startingValue.description + " (" + displayStringFromFHIRCodeableConcept(startingValue.type) + ")";
                } else {
                    startingComparisonGroupEntry.description = displayStringFromFHIRCodeableConcept(startingValue.type);
                }
            }
            if (Array.isArray(startingValue.intendedExposure) && startingValue.intendedExposure.length) {
                if (!startingComparisonGroupEntry.extension) {
                    startingComparisonGroupEntry.extension = [];
                }
                for (const instance of startingValue.intendedExposure) {
                    startingComparisonGroupEntry.extension.push({
                        "url": "http://hl7.org/fhir/5.0/StructureDefinition/extension-ResearchStudy.comparisonGroup.intendedExposure",
                        "valueReference": instance
                    })
                }
            }
        }
        setComparisonGroupEntryState(JSON.parse(JSON.stringify(startingComparisonGroupEntry)));
    }, []);

    useEffect((() => {
        if (Object.keys(comparisonGroupEntryState).length > 0) {
            let newGroupEntry = {};
            if (comparisonGroupEntryState.extension) { newGroupEntry.extension = comparisonGroupEntryState.extension; }
            if (comparisonGroupEntryState.name) { newGroupEntry.name = comparisonGroupEntryState.name; }
            if (comparisonGroupEntryState.targetNumber || comparisonGroupEntryState.targetNumber === 0) {
                newGroupEntry.targetNumber = comparisonGroupEntryState.targetNumber;
            }
            if (comparisonGroupEntryState.actualNumber || comparisonGroupEntryState.actualNumber === 0) {
                newGroupEntry.actualNumber = comparisonGroupEntryState.actualNumber;
            }
            if (comparisonGroupEntryState.eligibility && Object.keys(comparisonGroupEntryState.eligibility).length > 0) {
                newGroupEntry.eligibility = comparisonGroupEntryState.eligibility;
            }
            if (comparisonGroupEntryState.observedGroup && Object.keys(comparisonGroupEntryState.observedGroup).length > 0) {
                newGroupEntry.observedGroup = comparisonGroupEntryState.observedGroup;
            }
            if (comparisonGroupEntryState.description) { newGroupEntry.description = comparisonGroupEntryState.description; }
            setResourceState(prevState => { return { ...prevState, [elementName]: newGroupEntry }; });
        }
    }), [comparisonGroupEntryState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {startingValue.name && <p><b>Name: </b>{startingValue.name}</p>}
                {startingValue.description && <div><b>Description: </b><DisplayFromFHIRMarkdown markdown={startingValue.description} /><br /><br /></div>}
                {startingValue.targetNumber && <p><b>Target number of participants: </b>{startingValue.targetNumber}</p>}
                {(startingValue.actualNumber || startingValue.actualNumber === 0) && <p><b>Actual number of participants: </b>{startingValue.actualNumber}</p>}
                {startingValue.eligibility && <div><b>Eligibility Criteria: </b><DisplayFromFHIRReference reference={startingValue.eligibility}
                    containedResourceJson={startingValue.eligibility.reference &&
                        containedResourceDictionary[startingValue.eligibility.reference]} /><br /></div>}
                {Array.isArray(startingValue.intendedExposure) &&
                    startingValue.intendedExposure.map((intended, intendedIndex) => {
                        return <div key={intendedIndex}><b>Exposure: </b><DisplayFromFHIRReference reference={intended}
                            containedResourceJson={intended?.reference &&
                                containedResourceDictionary[intended.reference]} /></div>
                    })}
                {startingValue.observedGroup && <span><b>Observed Group: </b><DisplayFromFHIRReference reference={startingValue.observedGroup}
                    containedResourceJson={startingValue.observedGroup.reference &&
                        containedResourceDictionary[startingValue.observedGroup.reference]} /><br /></span>}
            </div>}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else if (Object.keys(comparisonGroupEntryState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <StringEntry elementName='name' fieldLabel='Name'
                    startingValue={comparisonGroupEntryState.name} setResourceState={setComparisonGroupEntryState} />
                <MarkdownEntry elementName='description' fieldLabel='Description'
                    startingValue={comparisonGroupEntryState.description} setResourceState={setComparisonGroupEntryState} />
                <UnsignedIntEntry elementName='targetNumber' fieldLabel='Target number of participants'
                    startingValue={comparisonGroupEntryState.targetNumber} setResourceState={setComparisonGroupEntryState} />
                <UnsignedIntEntry elementName='actualNumber' fieldLabel='Actual number of participants'
                    startingValue={comparisonGroupEntryState.actualNumber} setResourceState={setComparisonGroupEntryState} />
                <ReferenceEntry
                    elementName='eligibility' fieldLabel='Eligibility Criteria' startCollapsed={startCollapsed}
                    containedResourceDictionary={containedResourceDictionary}
                    referencedResourceTypes={['Group']} enableCreation={true}
                    startingValue={comparisonGroupEntryState.eligibility} setResourceState={setComparisonGroupEntryState} />
                <ReferenceEntry elementName='observedGroup' fieldLabel='Observed Group'
                    containedResourceDictionary={containedResourceDictionary}
                    referencedResourceTypes={['Group']} startCollapsed={startCollapsed}
                    startingValue={comparisonGroupEntryState.observedGroup} setResourceState={setComparisonGroupEntryState} />
            </div>
        </>
    }
}

const researchStudyDotLabelValueSet = [
    { system: "http://terminology.hl7.org/CodeSystem/title-type", code: "primary", display: "Primary title" },
    { system: "http://terminology.hl7.org/CodeSystem/title-type", code: "official", display: "Official title" },
    { system: "http://terminology.hl7.org/CodeSystem/title-type", code: "scientific", display: "Scientific title" },
    { system: "http://terminology.hl7.org/CodeSystem/title-type", code: "plain-language", display: "Plain language title" },
    { system: "http://terminology.hl7.org/CodeSystem/title-type", code: "subtitle", display: "Subtitle" },
    { system: "http://terminology.hl7.org/CodeSystem/title-type", code: "short-title", display: "Short title" },
    { system: "http://terminology.hl7.org/CodeSystem/title-type", code: "acronym", display: "Acronym" },
    { system: "http://terminology.hl7.org/CodeSystem/title-type", code: "earlier-title", display: "Different text in an earlier version" },
    { system: "http://terminology.hl7.org/CodeSystem/title-type", code: "language", display: "Different language" },
    { system: "http://terminology.hl7.org/CodeSystem/title-type", code: "autotranslated", display: "Different language derived from autotranslation" },
    { system: "http://terminology.hl7.org/CodeSystem/title-type", code: "human-use", display: "Human use" },
    { system: "http://terminology.hl7.org/CodeSystem/title-type", code: "machine-use", display: "Machine use" },
    { system: "http://terminology.hl7.org/CodeSystem/title-type", code: "duplicate-uid", display: "Different text for the same object with a different identifier" }
];
const ResearchStudyLabelEntry = ({ elementName, fieldLabel, startingValue, setResourceState, startCollapsed }) => {
    //TODO -- this ResearchStudyLabelEntry is no longer used and can be moved to unused functions, with removal of the call to it from ArrayEntry
    const [labelEntryState, setLabelEntryState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingLabelEntry = {
            "type": "", "language": "", "value": ""
        };
        if (startingValue) {
            if (startingValue.extension) { startingLabelEntry.extension = startingValue.extension; }
            if (startingValue.type) { startingLabelEntry.type = startingValue.type; }
            if (startingValue.language) { startingLabelEntry.language = startingValue.language; }
            if (startingValue.value) { startingLabelEntry.value = startingValue.value; }
        }
        setLabelEntryState(startingLabelEntry);
    }, []);

    useEffect((() => {
        if (Object.keys(labelEntryState).length > 0) {
            let newLabelEntry = {};
            if (labelEntryState.extension) { newLabelEntry.extension = labelEntryState.extension; }
            if (labelEntryState.type && Object.keys(labelEntryState.type).length > 0) {
                newLabelEntry.type = labelEntryState.type;
            }
            if (labelEntryState.language) { newLabelEntry.language = labelEntryState.language; }
            if (labelEntryState.value) { newLabelEntry.value = labelEntryState.value; }
            setResourceState(prevState => { return { ...prevState, [elementName]: newLabelEntry }; });
        }
    }), [labelEntryState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <p>
                <b>{simpleDisplayStringFromFHIRCodeableConcept(startingValue.type) ||
                    "Label"}{startingValue.language &&
                        ", Language -- " + languageCodeDisplay[startingValue.language]}: </b>{startingValue.value}
            </p>}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else if (Object.keys(labelEntryState).length > 0) {
        return <>
            {labelEntryState !== undefined && <>
                <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
                <div style={{ marginLeft: "24px" }}>
                    <StringEntry elementName='value' fieldLabel='Label value'
                        startingValue={labelEntryState.value} setResourceState={setLabelEntryState} />
                    <CodeableConceptEntry elementName='type' fieldLabel='Label type'
                        valueSet={researchStudyDotLabelValueSet} startCollapsed={startCollapsed}
                        codeableConceptLevelValueSet={convertValueSetToCodeableConceptSet(researchStudyDotLabelValueSet)}
                        startingValue={labelEntryState.type} setResourceState={setLabelEntryState} />
                    <CodeEntry elementName='language' fieldLabel='Language'
                        startingValue={labelEntryState.language} setResourceState={setLabelEntryState}
                        allowedValues={languageCodes} />
                </div>
            </>}
        </>
    }
}

const ResearchStudyLabelTableEntry = ({ startingValue, setResourceState, startCollapsed }) => {

    const [labelArrayEntryState, setLabelArrayEntryState] = useState([]);
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);
    const [titleTypeOptions, setTitleTypeOptions] = useState({ "options": generateValueSetOptions("titleTypeValueSet") });

    useEffect(() => {
        if (startingValue) {
            setLabelArrayEntryState(JSON.parse(JSON.stringify(startingValue)));
        }
    }, []);

    useEffect((() => {
        if (labelArrayEntryState.length > 0) {
            let newLabelEntry = JSON.parse(JSON.stringify(labelArrayEntryState));
            setResourceState(prevState => { return { ...prevState, "label": newLabelEntry }; });
        }
    }), [labelArrayEntryState]);

    if (startCollapsedState) {
        return <div>
            {startingValue?.length > 0 ?
                <div>
                    <p><b>Labels:</b> <ExpandToAddOrEdit startingValue={true} setStartCollapsedState={setStartCollapsedState} />
                    </p>
                    <div style={{ marginLeft: "24px" }}>
                        {startingValue.map((label, labelIndex) => {
                            return <p key={labelIndex}>
                                <b>{simpleDisplayStringFromFHIRCodeableConcept(label.type) ||
                                    "Label"}{label.language &&
                                        ", Language -- " + languageCodeDisplay[label.language]}: </b>{label.value}
                            </p>
                        })}
                    </div>
                </div>
                :
                <div>
                    <p><b>Labels:</b> None</p>
                    &nbsp;&nbsp;
                    <ExpandToAddOrEdit startingValue={false} setStartCollapsedState={setStartCollapsedState} />
                </div>
            }
        </div>
    }
    return <div>
        <h4>Labels:</h4>
        <div className={`CitationTitlesSubSection`} style={{ paddingLeft: "40px" }}>
            <Table>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>Label text</Table.HeaderCell>
                        <Table.HeaderCell>Type</Table.HeaderCell>
                        <Table.HeaderCell>Language</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {labelArrayEntryState?.length > 0 && labelArrayEntryState.map((labelx, index) => {
                        let label = JSON.parse(JSON.stringify(labelx));
                        if (Array.isArray(label.type)) {
                            for (let type of label.type) {
                                if (typeof type === 'object' && !Array.isArray(type) && Array.isArray(type.coding)) {
                                    for (let coding of type.coding) {
                                        if (coding.system === "http://hl7.org/fhir/title-type") {
                                            coding.system = "http://terminology.hl7.org/CodeSystem/title-type"
                                        }
                                    }
                                }
                            }
                        }
                        return <Table.Row key={index}>
                            <Table.Cell style={{ verticalAlign: "top" }}>
                                <TextField style={{ width: "100%" }} multiline
                                    className="inputField" type='text'
                                    label={'Label Text'} size="small" variant='outlined'
                                    value={label.value}
                                    onChange={(e) => {
                                        setLabelArrayEntryState(prevState => {
                                            let newLabelArray = JSON.parse(JSON.stringify(prevState));
                                            newLabelArray[index]["value"] = e.target.value;
                                            return newLabelArray;
                                        });
                                    }} />
                            </Table.Cell>
                            <Table.Cell style={{ verticalAlign: "top" }}>
                                <DropDownWithAdditionsLabelType currentValue={label.type || ""}
                                    labelText={'Pick Title Type'}
                                    options={titleTypeOptions.options}
                                    optionsLookup={generateValueSetLookup("titleTypeValueSet")}
                                    setOptions={setTitleTypeOptions}
                                    setLabelArrayEntryState={setLabelArrayEntryState}
                                    index={index}
                                />
                                {(titleTypeLookup[label.type?.coding?.[0]?.code]) &&
                                    <p><b>Tool tip:</b> used for {titleTypeLookup[label.type.coding[0].code].definition}</p>
                                }
                            </Table.Cell>
                            <Table.Cell style={{ verticalAlign: "top" }}>
                                <Dropdown placeholder={'Pick Language'}
                                    closeOnChange search selection
                                    selectOnBlur={false} multiple={false} clearable
                                    style={{ width: "50px" }}
                                    options={languageCodeOptions}
                                    value={typeof label.language === "string" ?
                                        label.language : label.language?.coding?.[0]?.code ?
                                            label.language.coding[0].code : label.language?.text ?
                                                label.language.text : ""}
                                    onChange={(e, data) => {
                                        setLabelArrayEntryState(prevState => {
                                            let newLabelArray = JSON.parse(JSON.stringify(prevState));
                                            newLabelArray[index]["language"] = data.value;
                                            return newLabelArray;
                                        });
                                    }}
                                />
                            </Table.Cell>
                        </Table.Row>;
                    })
                    }
                </Table.Body>
            </Table>
            <span>
                <Button className="formButton" style={{ color: "#000000" }}
                    content="+ Add Label"
                    onClick={() => {
                        setLabelArrayEntryState(prevState => {
                            let newLabelArray = prevState.concat([{}]);
                            return newLabelArray;
                        });
                    }}
                />
            </span>
        </div>
    </div>
}

const DropDownWithAdditionsLabelType = ({ currentValue, labelText, options, optionsLookup, setOptions,
    setLabelArrayEntryState, index, width }) => {

    if (width === undefined) {
        width = "100%";
    }

    const [selectedDropDownValueState, setSelectedDropDownValueState] = useState(null);
    const [startingSelectedDropDownValueState, setStartingSelectedDropDownValueState] = useState(null);

    useEffect(() => {
        if (currentValue === undefined) {
            currentValue = "";
        }
        let startingSelectedDropDownValue = "";
        if (typeof currentValue === "object") {
            startingSelectedDropDownValue = currentValue?.coding?.[0]?.code || currentValue?.text || currentValue.toString();
        } else if (typeof currentValue === "string") {
            startingSelectedDropDownValue = currentValue;
        } else {
            startingSelectedDropDownValue = currentValue.toString();
        }
        let startingValue = startingSelectedDropDownValue;
        setSelectedDropDownValueState(startingValue);
        setStartingSelectedDropDownValueState(startingValue);
        if (typeof startingValue === "string") {
            if (optionsLookup === undefined || optionsLookup[startingValue] === undefined) {
                setOptions(prevState => {
                    let newOptions;
                    let addNewOption = true;
                    for (let optionIndex in prevState.options) {
                        if (prevState.options[optionIndex].value === startingValue) {
                            addNewOption = false;
                            break;
                        }
                    }
                    if (addNewOption) {
                        newOptions = [{ text: startingValue, value: startingValue, key: startingValue }, ...prevState.options];
                    } else {
                        newOptions = prevState.options;
                    }
                    return {
                        ...prevState,
                        "options": newOptions,
                    }
                });
            }
        }
    }, []);

    useEffect(() => {
        if (selectedDropDownValueState !== null && !jsonEquals(selectedDropDownValueState, startingSelectedDropDownValueState)) {
            let newContent;
            if (optionsLookup[selectedDropDownValueState]) {
                newContent = {
                    "coding": [{
                        "system": optionsLookup[selectedDropDownValueState].system,
                        "code": selectedDropDownValueState,
                        "display": optionsLookup[selectedDropDownValueState].display
                    }]
                };
            } else {
                newContent = { "text": selectedDropDownValueState };
            }
            setLabelArrayEntryState(prevState => {
                let newArray = JSON.parse(JSON.stringify(prevState));
                newArray[index]["type"] = newContent;
                return newArray;
            });
        }
    }, [selectedDropDownValueState]);

    if (selectedDropDownValueState !== null) {
        return <Dropdown placeholder={labelText} closeOnChange search selection
            selectOnBlur={false} allowAdditions clearable
            style={{ width: width }} fluid
            options={options}
            value={selectedDropDownValueState}
            onAddItem={(e, data) => {
                if (optionsLookup === undefined || optionsLookup[data.value] === undefined) {
                    addOption(setOptions, data.value);
                }
            }}
            onChange={(e, data) => {
                setSelectedDropDownValueState(data.value);
            }}
        />
    }
};

const addOption = (setOptions, newValue) => {
    if (newValue) {
        console.log(newValue)
        setOptions(prevState => {
            let newOptions = prevState.options;
            let addNewOption = true;
            for (let optionIndex in newOptions) {
                if (newOptions[optionIndex].value === newValue) {
                    addNewOption = false;
                    break;
                }
            }
            if (addNewOption) {
                newOptions.push({ text: newValue, value: newValue, key: newValue });
            }
            return {
                ...prevState,
                "options": newOptions
            }
        });
    }
};

const EventHandlingEntry = ({ elementName, fieldLabel, startingValue, setResourceState }) => {

    const [eventHandlingState, setEventHandlingState] = useState({});

    useEffect(() => {
        let startingEventHandlingEntry = {
            event: "", group: "", handling: "", description: ""
        }
        if (startingValue) {
            if (startingValue.extension) {
                startingEventHandlingEntry.extension = startingValue.extension;
            }
            if (startingValue.event) {
                startingEventHandlingEntry.event = startingValue.event;
            }
            if (startingValue.group) {
                startingEventHandlingEntry.group = startingValue.group;
            }
            if (startingValue.handling) {
                startingEventHandlingEntry.handling = startingValue.handling;
            }
            if (startingValue.description) {
                startingEventHandlingEntry.description = startingValue.description;
            }
        }
        setEventHandlingState(JSON.parse(JSON.stringify(startingEventHandlingEntry)));
    }, []);

    useEffect((() => {
        if (Object.keys(eventHandlingState).length > 0) {
            let newEventHandling = {};
            if (eventHandlingState.extension) { newEventHandling.extension = eventHandlingState.extension; }
            if (eventHandlingState.event && Object.keys(eventHandlingState.event).length > 0) {
                newEventHandling.event = eventHandlingState.event;
            }
            if (eventHandlingState.group && Object.keys(eventHandlingState.group).length > 0) {
                newEventHandling.group = eventHandlingState.group;
            }
            if (eventHandlingState.handling && Object.keys(eventHandlingState.handling).length > 0) {
                newEventHandling.handling = eventHandlingState.handling;
            }
            if (eventHandlingState.description && Object.keys(eventHandlingState.description).length > 0) {
                newEventHandling.description = eventHandlingState.description;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newEventHandling }; });
        }
    }), [eventHandlingState]);

    if (Object.keys(eventHandlingState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <CodeableConceptEntry elementName='event' fieldLabel='Event'
                    codeableConceptLevelValueSet={[
                        { "text": "Discontinuation of treatment" },
                        { "text": "Follow-up non-adherence" },
                        { "text": "Terminal event" },
                        { "text": "Treatment non-adherence" },
                        { "text": "Treatment switching" },
                        { "text": "Use of an additional treatment" },
                        { "text": "Use of a prohibited treatment" }
                    ]}
                    startingValue={eventHandlingState.event} setResourceState={setEventHandlingState} />
                <CodeableConceptEntry elementName='group' fieldLabel='Group'
                    codeableConceptLevelValueSet={[
                        { "text": "All participants" },
                        { "text": "Participants assigned Intervention" },
                        { "text": "Participants assigned Comparator" },
                        { "text": "Participants who received Intervention" },
                        { "text": "Participants who received Comparator" }
                    ]}
                    startingValue={eventHandlingState.group} setResourceState={setEventHandlingState} />
                <CodeableConceptEntry elementName='handling' fieldLabel='Handling'
                    codeableConceptLevelValueSet={[
                        { "text": "Treatment policy strategy" },
                        { "text": "Composite strategy" },
                        { "text": "While on treatment strategy" },
                        { "text": "Hypothetical strategy" },
                        { "text": "Principal stratification" }
                    ]}
                    startingValue={eventHandlingState.handling} setResourceState={setEventHandlingState} />
                <MarkdownEntry elementName='description' fieldLabel='Description'
                    startingValue={eventHandlingState.description} setResourceState={setEventHandlingState} />
            </div>
        </>
    }
}

const StudyAmendmentEntry = ({ elementName, fieldLabel, startingValue, setResourceState, startCollapsed }) => {

    const [studyAmendmentState, setStudyAmendmentState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingStudyAmendment = {
            details: "",
            scope: "",
            description: "",
            rationale: "",
            substantialImpactSafety: "",
            substantialImpactSafetyComment: "",
            substantialImpactReliability: "",
            substantialImpactReliabilityComment: "",
            primaryReason: ""
        }
        if (startingValue?.extension) {
            for (const ext of startingValue.extension) {
                if (ext.url === "details") {
                    startingStudyAmendment.details = ext.valueString;
                } else if (ext.url === "scope") {
                    startingStudyAmendment.scope = ext.valueString;
                } else if (ext.url === "description") {
                    startingStudyAmendment.description = ext.valueString;
                } else if (ext.url === "rationale") {
                    startingStudyAmendment.rationale = ext.valueString;
                } else if (ext.url === "substantialImpactSafety") {
                    startingStudyAmendment.substantialImpactSafety = ext.valueBoolean;
                } else if (ext.url === "substantialImpactSafetyComment") {
                    startingStudyAmendment.substantialImpactSafetyComment = ext.valueString;
                } else if (ext.url === "substantialImpactReliability") {
                    startingStudyAmendment.substantialImpactReliability = ext.valueBoolean;
                } else if (ext.url === "substantialImpactReliabilityComment") {
                    startingStudyAmendment.substantialImpactReliabilityComment = ext.valueString;
                } else if (ext.url === "primaryReason") {
                    startingStudyAmendment.primaryReason = ext.valueCodeableConcept;
                }
            }
        }
        setStudyAmendmentState(JSON.parse(JSON.stringify(startingStudyAmendment)));
    }, []);

    useEffect((() => {
        if (Object.keys(studyAmendmentState).length > 0) {
            let newStudyAmendment = {
                extension: [], url: "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-study-amendment"
            };
            if (studyAmendmentState.details) {
                newStudyAmendment.extension.push({ "url": "details", "valueString": studyAmendmentState.details });
            }
            if (studyAmendmentState.scope) {
                newStudyAmendment.extension.push({ "url": "scope", "valueId": studyAmendmentState.scope });
            }
            if (studyAmendmentState.description) {
                newStudyAmendment.extension.push({ "url": "description", "valueString": studyAmendmentState.description });
            }
            if (studyAmendmentState.rationale) {
                newStudyAmendment.extension.push({ "url": "rationale", "valueString": studyAmendmentState.rationale });
            }
            if (studyAmendmentState.substantialImpactSafety === true || studyAmendmentState.substantialImpactSafety === false) {
                newStudyAmendment.extension.push({ "url": "substantialImpactSafety", "valueBoolean": studyAmendmentState.substantialImpactSafety });
            }
            if (studyAmendmentState.substantialImpactSafetyComment) {
                newStudyAmendment.extension.push({ "url": "substantialImpactSafetyComment", "valueString": studyAmendmentState.substantialImpactSafetyComment });
            }
            if (studyAmendmentState.substantialImpactReliability === true || studyAmendmentState.substantialImpactReliability === false) {
                newStudyAmendment.extension.push({ "url": "substantialImpactReliability", "valueBoolean": studyAmendmentState.substantialImpactReliability });
            }
            if (studyAmendmentState.substantialImpactReliabilityComment) {
                newStudyAmendment.extension.push({ "url": "substantialImpactReliabilityComment", "valueString": studyAmendmentState.substantialImpactReliabilityComment });
            }
            if (studyAmendmentState.primaryReason) {
                newStudyAmendment.extension.push({ "url": "primaryReason", "valueCodeableConcept": studyAmendmentState.primaryReason });
            }
            if (newStudyAmendment.extension.length) {
                setResourceState(prevState => { return { ...prevState, [elementName]: newStudyAmendment }; });
            }
        }
    }), [studyAmendmentState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {studyAmendmentState.details && <p><b>Details: </b>{studyAmendmentState.details}</p>}
                {studyAmendmentState.scope && <p><b>Scope: </b>{studyAmendmentState.scope}</p>}
                {studyAmendmentState.description && <p><b>Description: </b>{studyAmendmentState.description}</p>}
                {studyAmendmentState.rationale && <p><b>Rationale: </b>{studyAmendmentState.rationale}</p>}
                {studyAmendmentState.substantialImpactSafety === true && <p><b>Substantial Impact on Safety: </b>YES</p>}
                {studyAmendmentState.substantialImpactSafety === false && <p><b>Substantial Impact on Safety: </b>No</p>}
                {studyAmendmentState.substantialImpactSafetyComment && <p><b>Substantial Impact on Safety Comment: </b>{studyAmendmentState.substantialImpactSafetyComment}</p>}
                {studyAmendmentState.substantialImpactReliability === true && <p><b>Substantial Impact on Reliability: </b>YES</p>}
                {studyAmendmentState.substantialImpactReliability === false && <p><b>Substantial Impact on Reliability: </b>No</p>}
                {studyAmendmentState.substantialImpactReliabilityComment && <p><b>Substantial Impact on Reliability Comment: </b>{studyAmendmentState.substantialImpactReliabilityComment}</p>}
                {studyAmendmentState.primaryReason && <p><b>Primary Reason: </b>{displayStringFromFHIRCodeableConcept(studyAmendmentState.primaryReason)}</p>}
            </div>}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else if (Object.keys(studyAmendmentState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <StringEntry elementName='details' fieldLabel='Details'
                    startingValue={studyAmendmentState.details} setResourceState={setStudyAmendmentState} />
                <StringEntry elementName='scope' fieldLabel='Scope'
                    startingValue={studyAmendmentState.scope} setResourceState={setStudyAmendmentState} />
                <StringEntry elementName='description' fieldLabel='Description'
                    startingValue={studyAmendmentState.description} setResourceState={setStudyAmendmentState} />
                <StringEntry elementName='rationale' fieldLabel='Rationale'
                    startingValue={studyAmendmentState.rationale} setResourceState={setStudyAmendmentState} />
                <BooleanEntry elementName='substantialImpactSafety' fieldLabel='Substantial Impact on Safety'
                    storeFalse
                    startingValue={studyAmendmentState.substantialImpactSafety} setResourceState={setStudyAmendmentState} />
                <StringEntry elementName='substantialImpactSafetyComment' fieldLabel='Substantial Impact on Safety Comment'
                    startingValue={studyAmendmentState.substantialImpactSafetyComment} setResourceState={setStudyAmendmentState} />
                <BooleanEntry elementName='substantialImpactReliability' fieldLabel='Substantial Impact on Reliability'
                    storeFalse
                    startingValue={studyAmendmentState.substantialImpactReliability} setResourceState={setStudyAmendmentState} />
                <StringEntry elementName='substantialImpactReliabilityComment' fieldLabel='Substantial Impact on Reliability Comment'
                    startingValue={studyAmendmentState.substantialImpactReliabilityComment} setResourceState={setStudyAmendmentState} />
                <CodeableConceptEntry elementName='primaryReason' fieldLabel='Primary Reason'
                    startingValue={studyAmendmentState.primaryReason} setResourceState={setStudyAmendmentState} />
            </div>
        </>
    }
}

const researchStudyDotObjectiveTypeValueSet = [
    { system: "http://terminology.hl7.org/CodeSystem/research-study-objective-type", code: "primary", display: "Primary" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-objective-type", code: "secondary", display: "Secondary" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-objective-type", code: "exploratory", display: "Exploratory" }
];
const researchStudyDotObjectiveTypeCodeableConceptSet = [
    {
        "coding": [
            { system: "http://terminology.hl7.org/CodeSystem/research-study-objective-type", code: "primary", display: "Primary" },
        ]
    },
    {
        "coding": [
            { system: "http://terminology.hl7.org/CodeSystem/research-study-objective-type", code: "secondary", display: "Secondary" },
        ]
    },
    {
        "coding": [
            { system: "http://terminology.hl7.org/CodeSystem/research-study-objective-type", code: "exploratory", display: "Exploratory" }
        ]
    }
]
const ResearchStudyObjectiveEntry = ({ elementName, fieldLabel, startingValue, setResourceState,
    startCollapsed, containedResourceDictionary = {} }) => {

    const [objectiveEntryState, setObjectiveEntryState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);
    const [objectiveEndpointsState, setObjectiveEndpointsState] = useState({ "references": [], "loaded": false });

    useEffect(() => {
        let startingObjectiveEntry = {
            name: "", type: "", description: "", outcomeMeasure: []
        };
        if (startingValue) {
            if (startingValue.extension) { startingObjectiveEntry.extension = startingValue.extension; }
            if (startingValue.name) { startingObjectiveEntry.name = startingValue.name; }
            if (startingValue.type) { startingObjectiveEntry.type = startingValue.type; }
            if (startingValue.description) { startingObjectiveEntry.description = startingValue.description; }
            if (startingValue.outcomeMeasure) {
                startingObjectiveEntry.outcomeMeasure = startingValue.outcomeMeasure;
            }
        }
        if (!objectiveEndpointsState?.loaded) {
            let endpointReferences = [];
            if (startingObjectiveEntry.outcomeMeasure.length > 0) {
                for (const outcomeMeasure of startingObjectiveEntry.outcomeMeasure) {
                    if (outcomeMeasure.endpoint) {
                        endpointReferences.push(outcomeMeasure.endpoint);
                    } else {
                        endpointReferences.push({});
                    }
                }
            }
            setObjectiveEndpointsState({
                "references": endpointReferences, "loaded": true
            });
        }
        setObjectiveEntryState(JSON.parse(JSON.stringify(startingObjectiveEntry)));
    }, []);

    useEffect(() => {
        if (objectiveEndpointsState?.references?.length) {
            setObjectiveEntryState(prevState => {
                let newOutcomeMeasure = JSON.parse(JSON.stringify(prevState.outcomeMeasure)) || [];
                for (let index in objectiveEndpointsState.references) {
                    if (newOutcomeMeasure[index]) {
                        if (objectiveEndpointsState.references[index]) {
                            newOutcomeMeasure[index].endpoint = objectiveEndpointsState.references[index];
                        }
                    } else {
                        newOutcomeMeasure.push({
                            "endpoint": objectiveEndpointsState.references[index]
                        });
                    }
                }
                return { ...prevState, outcomeMeasure: newOutcomeMeasure }
            });
        }
    }, [objectiveEndpointsState]);

    useEffect((() => {
        if (Object.keys(objectiveEntryState).length > 0) {
            let newObjectiveEntry = {};
            if (objectiveEntryState.extension) { newObjectiveEntry.extension = objectiveEntryState.extension; }
            if (objectiveEntryState.name) { newObjectiveEntry.name = objectiveEntryState.name; }
            if (objectiveEntryState.type && Object.keys(objectiveEntryState.type).length > 0) {
                newObjectiveEntry.type = objectiveEntryState.type;
            }
            if (objectiveEntryState.description) { newObjectiveEntry.description = objectiveEntryState.description; }
            if (Array.isArray(objectiveEntryState.outcomeMeasure) && objectiveEntryState.outcomeMeasure.length) {
                newObjectiveEntry.outcomeMeasure = objectiveEntryState.outcomeMeasure;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newObjectiveEntry }; });
        }
    }), [objectiveEntryState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {startingValue.name && <p><b>Name: </b>{startingValue.name}</p>}
                {startingValue.type && <span><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.type} /><br /></span>}
                {startingValue.description && <div><b>Description: </b><DisplayFromFHIRMarkdown markdown={startingValue.description} /><br /><br /></div>}
                {startingValue.outcomeMeasure &&
                    <OutcomeMeasuresTable outcomeMeasure={startingValue.outcomeMeasure}
                        containedResourceDictionary={containedResourceDictionary} />
                }
                {/*Array.isArray(startingValue.outcomeMeasure) &&
                    startingValue.outcomeMeasure.map((outcomeMeasure, outcomeMeasureIndex) => {
                        let name = outcomeMeasure.name || "";
                        let type = outcomeMeasure.type || "";
                        let description = outcomeMeasure.description || "";
                        let endpoint = outcomeMeasure.endpoint || "";
                        let population = outcomeMeasure.population || "";
                        let intervention = outcomeMeasure.intervention || "";
                        let comparator = outcomeMeasure.comparator || "";
                        let summaryMeasure = outcomeMeasure.summaryMeasure || "";
                        let eventHandling = outcomeMeasure.eventHandling || [];
                        return <div key={outcomeMeasureIndex} >
                            <h4>Outcome Measure {outcomeMeasureIndex + 1}</h4>
                            <div style={{ marginLeft: "24px" }}>
                                {name && <p><b>Name: </b>{name}</p>}
                                {type && <span><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={type} /><br /></span>}
                                {description && <span><b>Description: </b><DisplayFromFHIRMarkdown markdown={description} /><br /></span>}
                                {endpoint && <span><b>Endpoint: </b><DisplayFromFHIRReference reference={endpoint} /><br /></span>}
                                {population && <span><b>Population: </b><DisplayFromFHIRReference reference={population} /><br /></span>}
                                {intervention && <span><b>Intervention Group: </b><DisplayFromFHIRReference reference={intervention} /><br /></span>}
                                {comparator && <span><b>Comparator Group: </b><DisplayFromFHIRReference reference={comparator} /><br /></span>}
                                {summaryMeasure && <span><b>Summary measure: </b><DisplayFromFHIRCodeableConcept codeableConcept={summaryMeasure} /><br /></span>}
                                {Array.isArray(eventHandling) &&
                                    eventHandling.map((ice, iceIndex) => {
                                        let event = ice.event || "";
                                        let group = ice.group || "";
                                        let handling = ice.handling || "";
                                        let description = ice.description || "";
                                        return <div key={iceIndex}>
                                            <h4>Handling of Intercurrent Event {ice.id || (iceIndex + 1)}</h4>
                                            <div style={{ marginLeft: "24px" }}>
                                                {event && <span><b>Event: </b><DisplayFromFHIRCodeableConcept codeableConcept={event} /><br /></span>}
                                                {group && <span><b>Group: </b><DisplayFromFHIRCodeableConcept codeableConcept={group} /><br /></span>}
                                                {handling && <span><b>Handling: </b><DisplayFromFHIRCodeableConcept codeableConcept={handling} /><br /></span>}
                                                {description && <span><b>Description: </b><DisplayFromFHIRMarkdown markdown={description} /><br /></span>}
                                            </div>
                                        </div>
                                    })}
                            </div>
                        </div>
                    })*/}
                {/*Array.isArray(startingValue.extension) &&
                    startingValue.extension.map((extension, extensionIndex) => {
                        if (extension.url === "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-estimand") {
                            let label;
                            let type
                            let endpoint;
                            let population;
                            let intervention;
                            let comparator;
                            let summaryMeasure;
                            let eventHandling = [];
                            for (const ext of extension.extension) {
                                if (ext.url === "label") {
                                    label = ext.valueString;
                                } else if (ext.url === "type") {
                                    type = ext.valueCodeableConcept;
                                } else if (ext.url === "endpoint") {
                                    endpoint = ext.valueReference;
                                } else if (ext.url === "population") {
                                    population = ext.valueReference;
                                } else if (ext.url === "interventionGroup" || ext.url === "intervention") {
                                    intervention = ext.valueId;
                                } else if (ext.url === "comparatorGroup" || ext.url === "comparator") {
                                    comparator = ext.valueId;
                                } else if (ext.url === "summaryMeasure") {
                                    summaryMeasure = ext.valueCodeableConcept;
                                } else if (ext.url === "eventHandling") {
                                    eventHandling.push(ext);
                                }
                            }
                            return <div key={extensionIndex} >
                                <h4>Estimand {extensionIndex + 1}</h4>
                                <div style={{ marginLeft: "24px" }}>
                                    {label && <p><b>Label: </b>{label}</p>}
                                    {type && <span><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={type} /><br /></span>}
                                    {endpoint && <span><b>Endpoint: </b><DisplayFromFHIRReference reference={endpoint} /><br /></span>}
                                    {population && <span><b>Population: </b><DisplayFromFHIRReference reference={population} /><br /></span>}
                                    {intervention && <p><b>Intervention Group: </b>Comparison group {intervention}</p>}
                                    {comparator && <p><b>Comparator Group: </b>Comparison group {comparator}</p>}
                                    {summaryMeasure && <span><b>Summary measure: </b><DisplayFromFHIRCodeableConcept codeableConcept={summaryMeasure} /><br /></span>}
                                    {Array.isArray(eventHandling) &&
                                        eventHandling.map((ice, iceIndex) => {
                                            let event;
                                            let group;
                                            let handling;
                                            let description;
                                            for (const extension of ice.extension) {
                                                if (extension.url === "event") {
                                                    event = extension.valueCodeableConcept;
                                                } else if (extension.url === "group") {
                                                    group = extension.valueCodeableConcept;
                                                } else if (extension.url === "handling") {
                                                    handling = extension.valueCodeableConcept;
                                                } else if (extension.url === "description") {
                                                    description = extension.valueMarkdown;
                                                }
                                            }
                                            return <div key={iceIndex}>
                                                <h4>Handling of Intercurrent Event {ice.id || (iceIndex + 1)}</h4>
                                                <div style={{ marginLeft: "24px" }}>
                                                    {event && <span><b>Event: </b><DisplayFromFHIRCodeableConcept codeableConcept={event} /><br /></span>}
                                                    {group && <span><b>Group: </b><DisplayFromFHIRCodeableConcept codeableConcept={group} /><br /></span>}
                                                    {handling && <span><b>Handling: </b><DisplayFromFHIRCodeableConcept codeableConcept={handling} /><br /></span>}
                                                    {description && <span><b>Description: </b><DisplayFromFHIRMarkdown markdown={description} /><br /></span>}
                                                </div>
                                            </div>
                                        })}
                                </div>
                            </div>
                        } else {
                            return <></>;
                        }
                    })*/}
            </div>}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else if (Object.keys(objectiveEntryState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <StringEntry elementName='name' fieldLabel='Name'
                    startingValue={objectiveEntryState.name} setResourceState={setObjectiveEntryState} />
                <CodeableConceptEntry elementName='type' fieldLabel='Type'
                    valueSet={researchStudyDotObjectiveTypeValueSet} startCollapsed={startCollapsed}
                    codeableConceptLevelValueSet={researchStudyDotObjectiveTypeCodeableConceptSet}
                    startingValue={objectiveEntryState.type} setResourceState={setObjectiveEntryState} />
                <MarkdownEntry elementName='description' fieldLabel='Description'
                    startCollapsed={startCollapsed}
                    startingValue={objectiveEntryState.description} setResourceState={setObjectiveEntryState} />
                <br />    <br />
                {objectiveEndpointsState?.loaded && <>
                    <p><b>To manage the outcome measure entries as a simple set of resource references:</b></p>
                    <div style={{ marginLeft: "24px" }}>
                        <ArrayEntry datatype='Reference' elementName='references'
                            fieldLabel={'The VariableDefinition Resource'}
                            startingValue={objectiveEndpointsState.references || []}
                            setProfile={"VariableDefinition"}
                            startingResourceType={"EvidenceVariable"}
                            referencedResourceTypes={["EvidenceVariable"]}
                            startCollapsed startEmptyArrayClosed enableCreation={true}
                            setResourceState={setObjectiveEndpointsState} />
                    </div>
                </>}
                {objectiveEntryState?.outcomeMeasure &&
                    <OutcomeMeasuresTable outcomeMeasure={objectiveEntryState.outcomeMeasure}
                        containedResourceDictionary={containedResourceDictionary}
                        setObjectiveEntryState={setObjectiveEntryState} />
                }
                {/*<ArrayEntry datatype="ResearchStudyObjectiveOutcomeMeasure"
                    elementName='outcomeMeasure' fieldLabel='Outcome Measure'
                    startingValue={objectiveEntryState.outcomeMeasure} setResourceState={setObjectiveEntryState}
                    startCollapsed={true} startEmptyArrayClosed={true} />*/}
            </div>
        </>
    }
}

const OutcomeMeasureTableRowEntry = ({ outcomeMeasure, setTableState, outcomeMeasureIndex, functionalEditMode,
    containedResourceDictionary }) => {

    const [tableRowState, setTableRowState] = useState({});

    useEffect(() => {
        let startingRow = {
            "name": "",
            "type": "",
            "description": "",
            "endpoint": "",
            "population": "",
            "intervention": "",
            "comparator": "",
            "summaryMeasure": "",
            "endpointAnalysisPlan": "",
            "eventHandling": []
        };
        if (outcomeMeasure.name) {
            startingRow.name = outcomeMeasure.name;
        }
        if (outcomeMeasure.type) {
            startingRow.type = outcomeMeasure.type;
        }
        if (outcomeMeasure.description) {
            startingRow.description = outcomeMeasure.description;
        }
        if (outcomeMeasure.endpoint) {
            startingRow.endpoint = outcomeMeasure.endpoint;
        }
        if (outcomeMeasure.population) {
            startingRow.population = outcomeMeasure.population;
        }
        if (outcomeMeasure.intervention) {
            startingRow.intervention = outcomeMeasure.intervention;
        }
        if (outcomeMeasure.comparator) {
            startingRow.comparator = outcomeMeasure.comparator;
        }
        if (outcomeMeasure.summaryMeasure) {
            startingRow.summaryMeasure = outcomeMeasure.summaryMeasure;
        }
        if (outcomeMeasure.endpointAnalysisPlan) {
            startingRow.endpointAnalysisPlan = outcomeMeasure.endpointAnalysisPlan;
        }
        if (outcomeMeasure.eventHandling) {
            startingRow.eventHandling = outcomeMeasure.eventHandling;
        }
        setTableRowState(startingRow);
    }, []);

    useEffect(() => {
        if (Object.keys(tableRowState).length > 0) {
            let newRow = {
                "name": "",
                "type": "",
                "description": "",
                "endpoint": "",
                "population": "",
                "intervention": "",
                "comparator": "",
                "summaryMeasure": "",
                "endpointAnalysisPlan": "",
                "eventHandling": []
            };
            if (tableRowState.name) {
                newRow.name = tableRowState.name;
            }
            if (tableRowState.type) {
                newRow.type = tableRowState.type;
            }
            if (tableRowState.description) {
                newRow.description = tableRowState.description;
            }
            if (tableRowState.endpoint) {
                newRow.endpoint = tableRowState.endpoint;
            }
            if (tableRowState.population) {
                newRow.population = tableRowState.population;
            }
            if (tableRowState.intervention) {
                newRow.intervention = tableRowState.intervention;
            }
            if (tableRowState.comparator) {
                newRow.comparator = tableRowState.comparator;
            }
            if (tableRowState.summaryMeasure) {
                newRow.summaryMeasure = tableRowState.summaryMeasure;
            }
            if (tableRowState.endpointAnalysisPlan) {
                newRow.endpointAnalysisPlan = tableRowState.endpointAnalysisPlan;
            }
            if (tableRowState.eventHandling?.length) {
                newRow.eventHandling = tableRowState.eventHandling;
            }
            setTableState(prevState => {
                let newTable = JSON.parse(JSON.stringify(prevState));
                newTable[outcomeMeasureIndex] = newRow;
                return newTable;
            })
        }
    }, [tableRowState])

    return <>
        <Table.Cell style={{ verticalAlign: "top" }}>
            {functionalEditMode ?
                <>
                    <StringEntry elementName='name' fieldLabel='Outcome Name'
                        startingValue={tableRowState.name}
                        setResourceState={setTableRowState} />
                    <CodeableConceptEntry elementName='type' fieldLabel='Type'
                        valueSet={researchStudyDotObjectiveTypeValueSet} startCollapsed={true}
                        startingValue={tableRowState.type} setResourceState={setTableRowState} />
                    <MarkdownEntry elementName='description' fieldLabel='Outcome Description'
                        startingValue={tableRowState.description} startCollapsed={true}
                        setResourceState={setTableRowState} />
                </>
                :
                <>
                    {tableRowState.name &&
                        <p><b>Name: </b>{tableRowState.name}</p>}
                    {tableRowState.type &&
                        <span><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={tableRowState.type} /><br /></span>}
                    {tableRowState.description &&
                        <span><b>Description: </b><DisplayFromFHIRMarkdown markdown={tableRowState.description} /><br /></span>}
                </>}
        </Table.Cell>
        <Table.Cell style={{ verticalAlign: "top", paddingLeft: "0px" }}>
            {functionalEditMode ?
                <>
                    <ReferenceEntry elementName='endpoint' fieldLabel='Endpoint'
                        containedResourceDictionary={containedResourceDictionary}
                        startingValue={tableRowState.endpoint}
                        startingResourceType="EvidenceVariable"
                        referencedResourceTypes={["EvidenceVariable"]}
                        enableCreation={true} startCollapsed={true}
                        setProfile={"VariableDefinition"}
                        setResourceState={setTableRowState} />
                    <CodeableConceptEntry elementName='summaryMeasure' fieldLabel='Summary Measure'
                        startingValue={tableRowState.summaryMeasure} startCollapsed={true}
                        valueSet={SEVCO.statisticType} codeableConceptLevelValueSet={statisticTypeCodeableConceptSet}
                        setResourceState={setTableRowState} />
                    <ReferenceEntry elementName='endpointAnalysisPlan' fieldLabel='Endpoint Analysis Plan'
                        containedResourceDictionary={containedResourceDictionary}
                        startingValue={tableRowState.endpointAnalysisPlan} startCollapsed={true}
                        startingResourceType="Evidence"
                        referencedResourceTypes={["Evidence"]}
                        enableCreation={true}
                        setProfile="EndpointAnalysisPlan"
                        setResourceState={setTableRowState} />
                </>
                :
                <>
                    {tableRowState.endpoint &&
                        <span><b>Endpoint: </b><DisplayFromFHIRReference reference={tableRowState.endpoint}
                            containedResourceJson={tableRowState.endpoint.reference &&
                                containedResourceDictionary[tableRowState.endpoint.reference]} /><br /></span>}
                    {tableRowState.summaryMeasure &&
                        <span><b>Summary measure: </b><DisplayFromFHIRCodeableConcept codeableConcept={tableRowState.summaryMeasure} /><br /></span>}
                    {tableRowState.endpointAnalysisPlan &&
                        <span><b>Endpoint Analysis Plan: </b><DisplayFromFHIRReference reference={tableRowState.endpointAnalysisPlan} /><br /></span>}

                </>}
        </Table.Cell>
        <Table.Cell style={{ verticalAlign: "top", whiteSpace: "pre-wrap" }}>
            {functionalEditMode ?
                <>
                    <ReferenceEntry elementName='population' fieldLabel='Population'
                        containedResourceDictionary={containedResourceDictionary}
                        startingValue={tableRowState.population} startCollapsed={true}
                        startingResourceType="Group"
                        referencedResourceTypes={["Group"]}
                        enableCreation={true}
                        selectProfile={["StudyGroup", "StudyEligibilityCriteria"]}
                        setResourceState={setTableRowState} />
                    <ReferenceEntry elementName='intervention' fieldLabel='Intervention Group'
                        containedResourceDictionary={containedResourceDictionary}
                        startingValue={tableRowState.intervention} startCollapsed={true}
                        startingResourceType="Group"
                        referencedResourceTypes={["Group"]}
                        enableCreation={true}
                        selectAnyProfile={true}
                        setResourceState={setTableRowState} />
                    <ReferenceEntry elementName='comparator' fieldLabel='Comparator Group'
                        containedResourceDictionary={containedResourceDictionary}
                        startingValue={tableRowState.comparator} startCollapsed={true}
                        startingResourceType="Group"
                        referencedResourceTypes={["Group"]}
                        enableCreation={true}
                        selectAnyProfile={true}
                        setResourceState={setTableRowState} />
                </>
                :
                <>
                    {tableRowState.population &&
                        <span><b>Population: </b><DisplayFromFHIRReference reference={tableRowState.population}
                            containedResourceJson={tableRowState.population.reference &&
                                containedResourceDictionary[tableRowState.population.reference]} /><br /></span>}
                    {tableRowState.intervention &&
                        <span><b>Intervention Group: </b><DisplayFromFHIRReference reference={tableRowState.intervention}
                            containedResourceJson={tableRowState.intervention.reference &&
                                containedResourceDictionary[tableRowState.intervention.reference]} /><br /></span>}
                    {tableRowState.comparator &&
                        <span><b>Comparator Group: </b><DisplayFromFHIRReference reference={tableRowState.comparator}
                            containedResourceJson={tableRowState.comparator.reference &&
                                containedResourceDictionary[tableRowState.comparator.reference]} /><br /></span>}
                </>}
        </Table.Cell>
        <Table.Cell style={{ verticalAlign: "top" }}>
            {functionalEditMode ?
                <>
                    <ArrayEntry datatype='EventHandling' elementName='eventHandling'
                        fieldLabel='Handling of Intercurrent Events' startEmptyArrayClosed={true}
                        startCollapsed={true}
                        startingValue={tableRowState.eventHandling} setResourceState={setTableRowState} />

                </>
                :
                <>
                    {Array.isArray(tableRowState.eventHandling) &&
                        tableRowState.eventHandling.map((ice, iceIndex) => {
                            let event = ice.event || "";
                            let group = ice.group || "";
                            let handling = ice.handling || "";
                            let description = ice.description || "";
                            return <div key={iceIndex}>
                                <h4>Handling of Intercurrent Event {ice.id || (iceIndex + 1)}</h4>
                                <div style={{ marginLeft: "24px" }}>
                                    {event && <span><b>Event: </b><DisplayFromFHIRCodeableConcept codeableConcept={event} /><br /></span>}
                                    {group && <span><b>Group: </b><DisplayFromFHIRCodeableConcept codeableConcept={group} /><br /></span>}
                                    {handling && <span><b>Handling: </b><DisplayFromFHIRCodeableConcept codeableConcept={handling} /><br /></span>}
                                    {description && <span><b>Description: </b><DisplayFromFHIRMarkdown markdown={description} /><br /></span>}
                                </div>
                            </div>
                        })}
                </>}
        </Table.Cell>
    </>
}

const OutcomeMeasureModal = ({ outcomeMeasureModalState, setOutcomeMeasureModalState, setTableState,
    containedResourceDictionary }) => {

    const [saveChangesState, setSaveChangesState] = useState(false);
    const [changeAvailableToSaveState, setChangeAvailableToSaveState] = useState(false);

    const [outcomeMeasureState, setOutcomeMeasureState] = useState({
        name: "",
        type: "",
        description: "",
        endpoint: "",
        population: "",
        intervention: "",
        comparator: "",
        summaryMeasure: "",
        endpointAnalysisPlan: "",
        eventHandling: []
    });

    useEffect(() => {
        if (outcomeMeasureState.name || outcomeMeasureState.endpoint) {
            setChangeAvailableToSaveState(true);
        }
        if (saveChangesState === true && (outcomeMeasureState.name || outcomeMeasureState.endpoint)) {
            let newOutcomeMeasure = {};
            if (outcomeMeasureState.name) {
                newOutcomeMeasure.name = outcomeMeasureState.name;
            }
            if (outcomeMeasureState.type) {
                newOutcomeMeasure.type = outcomeMeasureState.type;
            }
            if (outcomeMeasureState.description) {
                newOutcomeMeasure.description = outcomeMeasureState.description;
            }
            if (outcomeMeasureState.endpoint) {
                newOutcomeMeasure.endpoint = outcomeMeasureState.endpoint;
            }
            if (outcomeMeasureState.population) {
                newOutcomeMeasure.population = outcomeMeasureState.population;
            }
            if (outcomeMeasureState.intervention) {
                newOutcomeMeasure.intervention = outcomeMeasureState.intervention;
            }
            if (outcomeMeasureState.comparator) {
                newOutcomeMeasure.comparator = outcomeMeasureState.comparator;
            }
            if (outcomeMeasureState.summaryMeasure) {
                newOutcomeMeasure.summaryMeasure = outcomeMeasureState.summaryMeasure;
            }
            if (outcomeMeasureState.endpointAnalysisPlan) {
                newOutcomeMeasure.endpointAnalysisPlan = outcomeMeasureState.endpointAnalysisPlan;
            }
            if (outcomeMeasureState.eventHandling?.length > 0) {
                newOutcomeMeasure.eventHandling = outcomeMeasureState.eventHandling;
            }
            setTableState(prevState => {
                return [...prevState, newOutcomeMeasure]
            });
            setOutcomeMeasureModalState(prevState => { return { ...prevState, modalOpen: false }; });
        }
    }, [outcomeMeasureState, saveChangesState]);

    let modalContent = <div style={{
        paddingTop: "0px", paddingLeft: "20px", paddingRight: "20px",
        paddingBottom: "40px", width: "100%", height: "100%", overflow: "auto"
    }}>
        <span style={{
            position: "absolute", backgroundColor: "#FFFFFF", width: "calc(90vw - 38px)", zIndex: 100,
            paddingTop: "6px", paddingBottom: "6px"
        }} >
            <Button style={{
                color: changeAvailableToSaveState ? "#FFFFFF" : "#000000",
                width: "230px", float: "left"
            }}
                className="formButton" disabled={saveChangesState}
                content="Save Changes" positive={changeAvailableToSaveState}
                onClick={() => {
                    setSaveChangesState(true);
                }}
            />
            &nbsp;&nbsp;&nbsp;
            <Button style={{ color: "#000000", width: "180px", float: "right" }}
                className="formButton"
                content="Close Window"
                onClick={() => {
                    setOutcomeMeasureModalState(prevState => { return { ...prevState, modalOpen: false }; });
                }}
            />
        </span>
        <br /><br /><br />
        <div className='tableFixHead'>
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Enter a </span>name<span> for the </span>outcome measure<span>.</span>
            </div>
            <StringEntry elementName='name' fieldLabel='Outcome Name'
                startingValue={outcomeMeasureState.name}
                setResourceState={setOutcomeMeasureState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Classify the outcome as </span>Primary<span>, </span>Secondary<span>, or </span>Exploratory<span>. (optional)</span>
            </div>
            <CodeableConceptEntry elementName='type'
                valueSet={researchStudyDotObjectiveTypeValueSet}
                codeableConceptLevelValueSet={convertValueSetToCodeableConceptSet(researchStudyDotObjectiveTypeValueSet)}
                startingValue={outcomeMeasureState.type} setResourceState={setOutcomeMeasureState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Provide a </span>description<span> of the </span>outcome measure<span>. (optional)</span>
            </div>
            <MarkdownEntry elementName='description'
                startingValue={outcomeMeasureState.description}
                setResourceState={setOutcomeMeasureState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Create a </span>definition<span> of the </span>endpoint<span>. [Edit Resource Content for a structured VariableDefinition]</span>
            </div>
            <ReferenceEntryForEstimand elementName='endpoint' fieldLabel='Endpoint'
                containedResourceDictionary={containedResourceDictionary}
                startingValue={outcomeMeasureState.endpoint}
                startingResourceType="EvidenceVariable"
                referencedResourceTypes={["EvidenceVariable"]}
                enableCreation={true}
                setProfile={"VariableDefinition"}
                setResourceState={setOutcomeMeasureState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Select or describe the </span>summary measure<span> for this outcome measure.</span>
            </div>
            <CodeableConceptEntry elementName='summaryMeasure'
                startingValue={outcomeMeasureState.summaryMeasure}
                valueSet={SEVCO.statisticType} codeableConceptLevelValueSet={statisticTypeCodeableConceptSet}
                setResourceState={setOutcomeMeasureState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Define the </span>population<span> for this outcome measure. (optional)</span>
            </div>
            <ReferenceEntryForEstimand elementName='population' fieldLabel='Population'
                containedResourceDictionary={containedResourceDictionary}
                startingValue={outcomeMeasureState.population} startCollapsed={true}
                startingResourceType="Group"
                referencedResourceTypes={["Group"]}
                enableCreation={true}
                selectProfile={["CohortDefinition", "StudyEligibilityCriteria", "StudyGroup"]}
                setResourceState={setOutcomeMeasureState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Define the </span>intervention group<span> for this outcome measure. (optional)</span>
            </div>
            <ReferenceEntryForEstimand elementName='intervention' fieldLabel='Intervention Group'
                containedResourceDictionary={containedResourceDictionary}
                startingValue={outcomeMeasureState.intervention} startCollapsed={true}
                startingResourceType="Group"
                referencedResourceTypes={["Group"]}
                enableCreation={true}
                selectProfile={["CohortDefinition", "ConceptualExposureDefinition", "ExposureGroup"]}
                setResourceState={setOutcomeMeasureState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Define the </span>comparator group<span> for this outcome measure. (optional)</span>
            </div>
            <ReferenceEntryForEstimand elementName='comparator' fieldLabel='Comparator Group'
                containedResourceDictionary={containedResourceDictionary}
                startingValue={outcomeMeasureState.comparator} startCollapsed={true}
                startingResourceType="Group"
                referencedResourceTypes={["Group"]}
                enableCreation={true}
                selectProfile={["AbsenceOfExposureDefinition", "CohortDefinition", "ComparatorGroup", "ConceptualComparatorDefinition", "ConceptualExposureDefinition", "ExposureGroup"]}
                setResourceState={setOutcomeMeasureState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Define the </span>endpoint analysis plan<span>. [Edit Resource Content for a structured EndpointAnalysisPlan]</span>
            </div>
            <ReferenceEntryForEstimand elementName='endpointAnalysisPlan' fieldLabel='Endpoint Analysis Plan'
                containedResourceDictionary={containedResourceDictionary}
                startingValue={outcomeMeasureState.endpointAnalysisPlan}
                startingResourceType="Evidence"
                referencedResourceTypes={["Evidence"]}
                enableCreation={true}
                setProfile="EndpointAnalysisPlan"
                summaryMeasure={outcomeMeasureState.summaryMeasure}
                population={outcomeMeasureState.population}
                intervention={outcomeMeasureState.intervention}
                comparator={outcomeMeasureState.comparator}
                endpoint={outcomeMeasureState.endpoint}
                setResourceState={setOutcomeMeasureState} />
            <div className="h4Style h4EntryLabel" style={{ marginBottom: "0px" }}>
                <span>Describe the </span>handling of intercurrent events<span> for this outcome measure. (optional)</span>
            </div>
            <ArrayEntry datatype='EventHandling' elementName='eventHandling'
                fieldLabel='Handling of Intercurrent Events' startEmptyArrayClosed={true}
                startCollapsed={true}
                startingValue={outcomeMeasureState.eventHandling} setResourceState={setOutcomeMeasureState} />
        </div>
        <br /><br />
    </div>;

    return <>
        <Modal
            style={{ padding: "0px", margin: "0px" }}
            dimmer={<Modal.Dimmer style={{ backgroundColor: "#00000077" }} />}
            open={outcomeMeasureModalState?.modalOpen}
            centered={false}
            content={modalContent}
            className="viewmywork"
        />
    </>
}

const OutcomeMeasuresTable = ({ outcomeMeasure, setObjectiveEntryState, containedResourceDictionary = {} }) => {

    const editMode = useSelector(state => selectEditMode(state));

    const [tableState, setTableState] = useState([]);
    const [startingTableState, setStartingTableState] = useState([]);
    const [outcomeMeasureModalState, setOutcomeMeasureModalState] = useState({ modalOpen: false });

    useEffect(() => {
        let outcomeMeasureCopy = JSON.parse(JSON.stringify(outcomeMeasure));
        setTableState(outcomeMeasureCopy);
        setStartingTableState(outcomeMeasureCopy);
    }, []);

    useEffect(() => {
        if (setObjectiveEntryState && tableState &&
            JSON.stringify(tableState) !== JSON.stringify(startingTableState)) {
            setObjectiveEntryState(prevState => {
                return { ...prevState, "outcomeMeasure": JSON.parse(JSON.stringify(tableState)) };
            })
        }
    }, [tableState]);

    if (Array.isArray(tableState)) {
        return <div>
            {outcomeMeasureModalState.modalOpen === true &&
                <OutcomeMeasureModal outcomeMeasureModalState={outcomeMeasureModalState}
                    containedResourceDictionary={containedResourceDictionary}
                    setOutcomeMeasureModalState={setOutcomeMeasureModalState}
                    setTableState={setTableState} />}
            <Table>
                <Table.Header>
                    <Table.Row>
                        <Table.HeaderCell>Outcome Measure Name/Description</Table.HeaderCell>
                        <Table.HeaderCell>Endpoint/Summary Measure</Table.HeaderCell>
                        <Table.HeaderCell>Population/Treatment Groups</Table.HeaderCell>
                        <Table.HeaderCell>Handling of Intercurrent Events</Table.HeaderCell>
                    </Table.Row>
                </Table.Header>
                <Table.Body>
                    {tableState?.length > 0 ?
                        tableState.map((outcomeMeasure, outcomeMeasureIndex) => {
                            return <Table.Row key={outcomeMeasureIndex}>
                                <OutcomeMeasureTableRowEntry outcomeMeasure={outcomeMeasure}
                                    containedResourceDictionary={containedResourceDictionary}
                                    setTableState={setTableState} functionalEditMode={editMode && setObjectiveEntryState}
                                    outcomeMeasureIndex={outcomeMeasureIndex} />
                            </Table.Row>
                        })
                        :
                        <Table.Row>
                            <Table.Cell colSpan="4" style={{ textAlign: "center" }}>
                                <h4>There are no outcome measures created yet.</h4>
                            </Table.Cell>
                        </Table.Row>
                    }
                    {(editMode && setObjectiveEntryState) &&
                        <Table.Row>
                            <Table.Cell colSpan="3" style={{ textAlign: "right" }}>
                                <Button className="formButton" content="Use the Guide to Add Outcome Measure with Endpoint Analysis Plan"
                                    style={{ color: "#000000" }} compact
                                    onClick={() => {
                                        setOutcomeMeasureModalState(prevState => { return { ...prevState, modalOpen: true }; });
                                    }} />
                            </Table.Cell>
                            <Table.Cell style={{ textAlign: "right" }}>
                                <Button className="formButton" content="Add Outcome Measure"
                                    style={{ color: "#000000" }} compact
                                    onClick={() => {
                                        setTableState(prevState => {
                                            return [...prevState, {
                                                name: "",
                                                type: "",
                                                description: "",
                                                endpoint: "",
                                                population: "",
                                                intervention: "",
                                                comparator: "",
                                                summaryMeasure: "",
                                                endpointAnalysisPlan: "",
                                                eventHandling: []
                                            }]
                                        });
                                    }} />
                            </Table.Cell>
                        </Table.Row>
                    }
                </Table.Body>
            </Table>
        </div>
    } else {
        return <div><p>No outcome measures.</p></div>
    }
}
/*
const ResearchStudyObjectiveOutcomeMeasureEntry = ({ elementName, fieldLabel, startingValue, setResourceState, startCollapsed }) => {

    const [outcomeMeasureEntryState, setOutcomeMeasureEntryState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingOutcomeEntry = {
            name: "", type: "", description: "", endpoint: "",
            population: "", intervention: "", comparator: "", summaryMeasure: "", eventHandling: []
        };
        if (startingValue) {
            if (startingValue.extension) { startingOutcomeEntry.extension = startingValue.extension; }
            if (startingValue.name) { startingOutcomeEntry.name = startingValue.name; }
            if (startingValue.type) { startingOutcomeEntry.type = startingValue.type; }
            if (startingValue.description) { startingOutcomeEntry.description = startingValue.description; }
            if (startingValue.endpoint) { startingOutcomeEntry.endpoint = startingValue.endpoint; }
            if (startingValue.population) { startingOutcomeEntry.population = startingValue.population; }
            if (startingValue.intervention) { startingOutcomeEntry.intervention = startingValue.intervention; }
            if (startingValue.comparator) { startingOutcomeEntry.comparator = startingValue.comparator; }
            if (startingValue.summaryMeasure) { startingOutcomeEntry.summaryMeasure = startingValue.summaryMeasure; }
            if (startingValue.eventHandling) { startingOutcomeEntry.eventHandling = startingValue.eventHandling; }
        }
        setOutcomeMeasureEntryState(JSON.parse(JSON.stringify(startingOutcomeEntry)));
    }, []);

    useEffect((() => {
        if (Object.keys(outcomeMeasureEntryState).length > 0) {
            let newOutcomeEntry = {};
            if (outcomeMeasureEntryState.extension) { newOutcomeEntry.extension = outcomeMeasureEntryState.extension; }
            if (outcomeMeasureEntryState.name) { newOutcomeEntry.name = outcomeMeasureEntryState.name; }
            if (outcomeMeasureEntryState.type && Object.keys(outcomeMeasureEntryState.type).length > 0) {
                newOutcomeEntry.type = outcomeMeasureEntryState.type;
            }
            if (outcomeMeasureEntryState.description) { newOutcomeEntry.description = outcomeMeasureEntryState.description; }
            if (outcomeMeasureEntryState.endpoint && Object.keys(outcomeMeasureEntryState.endpoint).length > 0) {
                newOutcomeEntry.endpoint = outcomeMeasureEntryState.endpoint;
            }
            if (outcomeMeasureEntryState.population && Object.keys(outcomeMeasureEntryState.population).length > 0) {
                newOutcomeEntry.population = outcomeMeasureEntryState.population;
            }
            if (outcomeMeasureEntryState.intervention && Object.keys(outcomeMeasureEntryState.intervention).length > 0) {
                newOutcomeEntry.intervention = outcomeMeasureEntryState.intervention;
            }
            if (outcomeMeasureEntryState.comparator && Object.keys(outcomeMeasureEntryState.comparator).length > 0) {
                newOutcomeEntry.comparator = outcomeMeasureEntryState.comparator;
            }
            if (outcomeMeasureEntryState.summaryMeasure && Object.keys(outcomeMeasureEntryState.summaryMeasure).length > 0) {
                newOutcomeEntry.summaryMeasure = outcomeMeasureEntryState.summaryMeasure;
            }
            if (Array.isArray(outcomeMeasureEntryState.eventHandling) && outcomeMeasureEntryState.eventHandling.length > 0) {
                newOutcomeEntry.eventHandling = outcomeMeasureEntryState.eventHandling;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newOutcomeEntry }; });
        }
    }), [outcomeMeasureEntryState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {startingValue.name && <p><b>Name: </b>{startingValue.name}</p>}
                {startingValue.type && <span><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.type} /><br /></span>}
                {startingValue.description && <div><b>Description: </b><DisplayFromFHIRMarkdown markdown={startingValue.description} /><br /><br /></div>}
                {startingValue.endpoint && <span><b>Endpoint: </b><DisplayFromFHIRReference reference={startingValue.endpoint} /><br /></span>}
                {startingValue.population && <span><b>Population: </b><DisplayFromFHIRReference reference={startingValue.population} /><br /></span>}
                {startingValue.intervention && <span><b>Intervention Group: </b><DisplayFromFHIRReference reference={startingValue.intervention} /><br /></span>}
                {startingValue.comparator && <span><b>Comparator Group: </b><DisplayFromFHIRReference reference={startingValue.comparator} /><br /></span>}
                {startingValue.summaryMeasure && <span><b>Summary measure: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.summaryMeasure} /><br /></span>}
                {Array.isArray(startingValue.eventHandling) &&
                    startingValue.eventHandling.map((ice, iceIndex) => {
                        let event = ice.event || "";
                        let group = ice.group || "";
                        let handling = ice.handling || "";
                        let description = ice.description || "";
                        return <div key={iceIndex}>
                            <h4>Handling of Intercurrent Event {ice.id || (iceIndex + 1)}</h4>
                            <div style={{ marginLeft: "24px" }}>
                                {event && <span><b>Event: </b><DisplayFromFHIRCodeableConcept codeableConcept={event} /><br /></span>}
                                {group && <span><b>Group: </b><DisplayFromFHIRCodeableConcept codeableConcept={group} /><br /></span>}
                                {handling && <span><b>Handling: </b><DisplayFromFHIRCodeableConcept codeableConcept={handling} /><br /></span>}
                                {description && <span><b>Description: </b><DisplayFromFHIRMarkdown markdown={description} /><br /></span>}
                            </div>
                        </div>
                    })}
                {Array.isArray(startingValue.extension) &&
                    startingValue.extension.map((extension, extensionIndex) => {
                        if (extension.url === "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-estimand") {
                            let label;
                            let type
                            let endpoint;
                            let population;
                            let intervention;
                            let comparator;
                            let summaryMeasure;
                            let eventHandling = [];
                            for (const ext of extension.extension) {
                                if (ext.url === "label") {
                                    label = ext.valueString;
                                } else if (ext.url === "type") {
                                    type = ext.valueCodeableConcept;
                                } else if (ext.url === "endpoint") {
                                    endpoint = ext.valueReference;
                                } else if (ext.url === "population") {
                                    population = ext.valueReference;
                                } else if (ext.url === "interventionGroup" || ext.url === "intervention") {
                                    intervention = ext.valueId;
                                } else if (ext.url === "comparatorGroup" || ext.url === "comparator") {
                                    comparator = ext.valueId;
                                } else if (ext.url === "summaryMeasure") {
                                    summaryMeasure = ext.valueCodeableConcept;
                                } else if (ext.url === "eventHandling") {
                                    eventHandling.push(ext);
                                }
                            }
                            return <div key={extensionIndex} >
                                <h4>Estimand {extensionIndex + 1}</h4>
                                <div style={{ marginLeft: "24px" }}>
                                    {label && <p><b>Label: </b>{label}</p>}
                                    {type && <span><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={type} /><br /></span>}
                                    {endpoint && <span><b>Endpoint: </b><DisplayFromFHIRReference reference={endpoint} /><br /></span>}
                                    {population && <span><b>Population: </b><DisplayFromFHIRReference reference={population} /><br /></span>}
                                    {intervention && <p><b>Intervention Group: </b>Comparison group {intervention}</p>}
                                    {comparator && <p><b>Comparator Group: </b>Comparison group {comparator}</p>}
                                    {summaryMeasure && <span><b>Summary measure: </b><DisplayFromFHIRCodeableConcept codeableConcept={summaryMeasure} /><br /></span>}
                                    {Array.isArray(eventHandling) &&
                                        eventHandling.map((ice, iceIndex) => {
                                            let event;
                                            let group;
                                            let handling;
                                            let description;
                                            for (const extension of ice.extension) {
                                                if (extension.url === "event") {
                                                    event = extension.valueCodeableConcept;
                                                } else if (extension.url === "group") {
                                                    group = extension.valueCodeableConcept;
                                                } else if (extension.url === "handling") {
                                                    handling = extension.valueCodeableConcept;
                                                } else if (extension.url === "description") {
                                                    description = extension.valueMarkdown;
                                                }
                                            }
                                            return <div key={iceIndex}>
                                                <h4>Handling of Intercurrent Event {ice.id || (iceIndex + 1)}</h4>
                                                <div style={{ marginLeft: "24px" }}>
                                                    {event && <span><b>Event: </b><DisplayFromFHIRCodeableConcept codeableConcept={event} /><br /></span>}
                                                    {group && <span><b>Group: </b><DisplayFromFHIRCodeableConcept codeableConcept={group} /><br /></span>}
                                                    {handling && <span><b>Handling: </b><DisplayFromFHIRCodeableConcept codeableConcept={handling} /><br /></span>}
                                                    {description && <span><b>Description: </b><DisplayFromFHIRMarkdown markdown={description} /><br /></span>}
                                                </div>
                                            </div>
                                        })}
                                </div>
                            </div>
                        } else {
                            return <></>;
                        }
                    })}
            </div>}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else if (Object.keys(outcomeMeasureEntryState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <StringEntry elementName='name' fieldLabel='Name'
                    startingValue={outcomeMeasureEntryState.name} setResourceState={setOutcomeMeasureEntryState} />
                <CodeableConceptEntry elementName='type' fieldLabel='Type'
                    valueSet={researchStudyDotObjectiveTypeValueSet} startCollapsed={startCollapsed}
                    startingValue={outcomeMeasureEntryState.type} setResourceState={setOutcomeMeasureEntryState} />
                <MarkdownEntry elementName='description' fieldLabel='Description'
                    startCollapsed={startCollapsed}
                    startingValue={outcomeMeasureEntryState.description} setResourceState={setOutcomeMeasureEntryState} />
                <ReferenceEntry elementName='endpoint' fieldLabel='Endpoint'
                    referencedResourceTypes={['EvidenceVariable']}
                    startingResourceType={"EvidenceVariable"}
                    enableCreation={true}
                    startingValue={outcomeMeasureEntryState.endpoint} setResourceState={setOutcomeMeasureEntryState} />
                <ReferenceEntry elementName='population' fieldLabel='Population'
                    referencedResourceTypes={['Group']}
                    startingResourceType={"Group"}
                    enableCreation={true}
                    startingValue={outcomeMeasureEntryState.population} setResourceState={setOutcomeMeasureEntryState} />
                <ReferenceEntry elementName='intervention' fieldLabel='Intervention Group'
                    referencedResourceTypes={['Group']}
                    startingValue={outcomeMeasureEntryState.population} setResourceState={setOutcomeMeasureEntryState} />
                <ReferenceEntry elementName='comparator' fieldLabel='Comparator Group'
                    referencedResourceTypes={['Group']}
                    startingValue={outcomeMeasureEntryState.population} setResourceState={setOutcomeMeasureEntryState} />
                <CodeableConceptEntry elementName='summaryMeasure' fieldLabel='Summary Measure'
                    valueSet={SEVCO.statisticType} codeableConceptLevelValueSet={statisticTypeCodeableConceptSet}
                    startingValue={outcomeMeasureEntryState.summaryMeasure} setResourceState={setOutcomeMeasureEntryState} />
                <ArrayEntry datatype='EventHandling' elementName='eventHandling'
                    fieldLabel='Handling of Intercurrent Events' startEmptyArrayClosed={true}
                    startingValue={outcomeMeasureEntryState.eventHandling} setResourceState={setOutcomeMeasureEntryState} />
            </div>
        </>
    }
}
*/
const ResearchStudyOutcomeMeasureEntry = ({ elementName, fieldLabel, startingValue, setResourceState,
    startCollapsed, containedResourceDictionary = {} }) => {

    const [outcomeMeasureEntryState, setOutcomeMeasureEntryState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingOutcomeEntry = {
            name: "", type: "", description: "", reference: ""
        };
        if (startingValue) {
            if (startingValue.extension) { startingOutcomeEntry.extension = startingValue.extension; }
            if (startingValue.name) { startingOutcomeEntry.name = startingValue.name; }
            if (startingValue.type) { startingOutcomeEntry.type = startingValue.type; }
            if (startingValue.description) { startingOutcomeEntry.description = startingValue.description; }
            if (startingValue.reference) { startingOutcomeEntry.reference = startingValue.reference; }
        }
        setOutcomeMeasureEntryState(JSON.parse(JSON.stringify(startingOutcomeEntry)));
    }, []);

    useEffect((() => {
        if (Object.keys(outcomeMeasureEntryState).length > 0) {
            let newOutcomeEntry = {};
            if (outcomeMeasureEntryState.extension) { newOutcomeEntry.extension = outcomeMeasureEntryState.extension; }
            if (outcomeMeasureEntryState.name) { newOutcomeEntry.name = outcomeMeasureEntryState.name; }
            if (outcomeMeasureEntryState.type && Object.keys(outcomeMeasureEntryState.type).length > 0) {
                newOutcomeEntry.type = outcomeMeasureEntryState.type;
            }
            if (outcomeMeasureEntryState.description) { newOutcomeEntry.description = outcomeMeasureEntryState.description; }
            if (outcomeMeasureEntryState.reference && Object.keys(outcomeMeasureEntryState.reference).length > 0) {
                newOutcomeEntry.reference = outcomeMeasureEntryState.reference;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newOutcomeEntry }; });
        }
    }), [outcomeMeasureEntryState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div style={{ marginLeft: "24px" }}>
                {startingValue.name && <p><b>Name: </b>{startingValue.name}</p>}
                {Array.isArray(startingValue.type) &&
                    startingValue.type.map((type, typeIndex) => {
                        return <div key={typeIndex}><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={type} /></div>
                    })}
                {startingValue.description && <div><b>Description: </b><DisplayFromFHIRMarkdown markdown={startingValue.description} /><br /><br /></div>}
                {startingValue.reference && <span><b>Outcome Definition: </b><DisplayFromFHIRReference reference={startingValue.reference}
                    containedResourceJson={startingValue.reference.reference &&
                        containedResourceDictionary[startingValue.reference.reference]} /><br /></span>}
            </div>}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else if (Object.keys(outcomeMeasureEntryState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <StringEntry elementName='name' fieldLabel='Name'
                    startingValue={outcomeMeasureEntryState.name} setResourceState={setOutcomeMeasureEntryState} />
                <ArrayEntry datatype='CodeableConcept' elementName='type' fieldLabel='Type'
                    valueSet={researchStudyDotObjectiveTypeValueSet} startCollapsed={startCollapsed}
                    startingValue={outcomeMeasureEntryState.type} setResourceState={setOutcomeMeasureEntryState} />
                <MarkdownEntry elementName='description' fieldLabel='Description'
                    startCollapsed={startCollapsed}
                    startingValue={outcomeMeasureEntryState.description} setResourceState={setOutcomeMeasureEntryState} />
                <ReferenceEntry elementName='reference' fieldLabel='Outcome Definition'
                    containedResourceDictionary={containedResourceDictionary}
                    referencedResourceTypes={['EvidenceVariable']} startCollapsed={startCollapsed}
                    startingValue={outcomeMeasureEntryState.reference} setResourceState={setOutcomeMeasureEntryState} />
            </div>
        </>
    }
}

const researchStudyRegistrationActivityValueSet = [
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "record-verification", display: "Record Verification" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "primary-outcome-data-collection", display: "Primary outcome data collection" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "registration-submission", display: "Registration submission" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "registration-submission-qc", display: "Registration submission Quality Check" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "registration-posting", display: "Registration posting" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "results-submission", display: "Results submission" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "results-submission-qc", display: "Results submission Quality Check" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "results-posting", display: "Results posting" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "disposition-submission", display: "Disposition submission" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "disposition-submission-qc", display: "Disposition submission Quality Check" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "disposition-posting", display: "Disposition posting" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "update-submission", display: "Update submission" },
    { system: "http://hl7.org/fhir/research-study-registration-activity", code: "update-posting", display: "Update posting" }
];

const researchStudyDotProgressStatusValueSet = [
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "overall-study", display: "Overall study" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "active", display: "Active" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "active-but-not-recruiting", display: "Active, not recruiting" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "administratively-completed", display: "Administratively Completed" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "approved", display: "Approved" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "closed-to-accrual", display: "Closed to Accrual" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "closed-to-accrual-and-intervention", display: "Closed to Accrual and Intervention" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "completed", display: "Completed" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "disapproved", display: "Disapproved" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "enrolling-by-invitation", display: "Enrolling by invitation" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "in-review", display: "In Review" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "not-yet-recruiting", display: "Not yet recruiting" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "recruiting", display: "Recruiting" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "temporarily-closed-to-accrual", display: "Temporarily Closed to Accrual" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "temporarily-closed-to-accrual-and-intervention", display: "Temporarily Closed to Accrual and Intervention" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "terminated", display: "Terminated" },
    { system: "http://terminology.hl7.org/CodeSystem/research-study-status", code: "withdrawn", display: "Withdrawn" }
].concat(researchStudyRegistrationActivityValueSet);

const ResearchStudyProgressStatusEntry = ({ elementName, fieldLabel, startingValue, setResourceState, startCollapsed }) => {

    const [progressStatusEntryState, setProgressStatusEntryState] = useState({});
    const [startCollapsedState, setStartCollapsedState] = useState(startCollapsed);

    useEffect(() => {
        let startingProgressStatusEntry = {
            state: "", actual: "", period: ""
        };
        if (startingValue) {
            if (startingValue.extension) { startingProgressStatusEntry.extension = startingValue.extension; }
            if (startingValue.state) { startingProgressStatusEntry.state = startingValue.state; }
            if (typeof startingValue.actual === "boolean") { startingProgressStatusEntry.actual = startingValue.actual; }
            if (startingValue.period) { startingProgressStatusEntry.period = startingValue.period; }
        }
        setProgressStatusEntryState(JSON.parse(JSON.stringify(startingProgressStatusEntry)));
    }, []);

    useEffect((() => {
        if (Object.keys(progressStatusEntryState).length > 0) {
            let newProgressEntry = {};
            if (progressStatusEntryState.extension) { newProgressEntry.extension = progressStatusEntryState.extension; }
            if (progressStatusEntryState.state && Object.keys(progressStatusEntryState.state).length > 0) {
                newProgressEntry.state = progressStatusEntryState.state;
            }
            if (typeof progressStatusEntryState.actual === "boolean") {
                newProgressEntry.actual = progressStatusEntryState.actual;
            }
            if (progressStatusEntryState.period && Object.keys(progressStatusEntryState.period).length > 0) {
                newProgressEntry.period = progressStatusEntryState.period;
            }
            setResourceState(prevState => { return { ...prevState, [elementName]: newProgressEntry }; });
        }
    }), [progressStatusEntryState]);

    if (startCollapsedState) {
        return <div>
            <b>{fieldLabel}: </b>
            {startingValue && <div>
                <div style={{ marginLeft: "24px" }}>
                    {startingValue.state && <span><b>State: </b><DisplayFromFHIRCodeableConcept codeableConcept={startingValue.state} /><br /></span>}
                    {startingValue.actual === true && <p><b>Actual</b></p>}
                    {startingValue.actual === false && <p><b>Anticipated</b></p>}
                    {startingValue.period && <p><b>Period: </b>{displayStringFromFHIRPeriod(startingValue.period)}</p>}
                </div>
            </div>}
            &nbsp;&nbsp;
            <ExpandToAddOrEdit startingValue={startingValue} setStartCollapsedState={setStartCollapsedState} />
        </div>
    } else if (Object.keys(progressStatusEntryState).length > 0) {
        return <>
            <p style={{ marginBottom: "0px" }}><b>{fieldLabel}: </b></p>
            <div style={{ marginLeft: "24px" }}>
                <CodeableConceptEntry elementName='state' fieldLabel='Progress state'
                    valueSet={researchStudyDotProgressStatusValueSet}
                    startingValue={progressStatusEntryState.state} setResourceState={setProgressStatusEntryState} />
                <BooleanEntry elementName='actual' fieldLabel='Actual (false if Anticipated)'
                    storeFalse={true}
                    startingValue={progressStatusEntryState.actual} setResourceState={setProgressStatusEntryState} />
                <PeriodEntry elementName='period' fieldLabel='Period (date range)'
                    startingValue={progressStatusEntryState.period} setResourceState={setProgressStatusEntryState} />
            </div>
        </>
    }
}

const ResearchStudyRecruitmentEntry = ({ elementName, fieldLabel, startingValue, setResourceState,
    startCollapsed, containedResourceDictionary = {} }) => {

    const [recruitmentEntryState, setRecruitmentEntryState] = useState({});

    useEffect(() => {
        let startingRecruitmentEntry = {
            targetNumber: "", actualNumber: "", eligibility: "", actualGroup: "", description: ""
        };
        if (startingValue) {
            if (startingValue.extension) { startingRecruitmentEntry.extension = startingValue.extension; }
            if (startingValue.targetNumber || startingValue.targetNumber === 0) { startingRecruitmentEntry.targetNumber = startingValue.targetNumber; }
            if (startingValue.actualNumber || startingValue.actualNumber === 0) { startingRecruitmentEntry.actualNumber = startingValue.actualNumber; }
            if (startingValue.eligibility) { startingRecruitmentEntry.eligibility = startingValue.eligibility; }
            if (startingValue.actualGroup) { startingRecruitmentEntry.actualGroup = startingValue.actualGroup; }
            if (startingValue.description) { startingRecruitmentEntry.description = startingValue.description; }
        }
        setRecruitmentEntryState(JSON.parse(JSON.stringify(startingRecruitmentEntry)));
    }, []);

    useEffect((() => {
        if (Object.keys(recruitmentEntryState).length > 0) {
            let newRecruitmentEntry = {};
            if (recruitmentEntryState.extension) { newRecruitmentEntry.extension = recruitmentEntryState.extension; }
            if (recruitmentEntryState.targetNumber || recruitmentEntryState.targetNumber === 0) { newRecruitmentEntry.targetNumber = recruitmentEntryState.targetNumber; }
            if (recruitmentEntryState.actualNumber || recruitmentEntryState.actualNumber === 0) { newRecruitmentEntry.actualNumber = recruitmentEntryState.actualNumber; }
            if (recruitmentEntryState.eligibility && Object.keys(recruitmentEntryState.eligibility).length > 0) {
                newRecruitmentEntry.eligibility = recruitmentEntryState.eligibility;
            }
            if (recruitmentEntryState.actualGroup && Object.keys(recruitmentEntryState.actualGroup).length > 0) {
                newRecruitmentEntry.actualGroup = recruitmentEntryState.actualGroup;
            }
            if (recruitmentEntryState.description) { newRecruitmentEntry.description = recruitmentEntryState.description; }
            setResourceState(prevState => { return { ...prevState, [elementName]: newRecruitmentEntry }; });
        }
    }), [recruitmentEntryState]);

    if (Object.keys(recruitmentEntryState).length > 0) {
        return <>
            <div style={{ marginLeft: "2px" }}>
                <UnsignedIntEntry elementName='targetNumber' fieldLabel='Target number of participants'
                    startingValue={recruitmentEntryState.targetNumber} setResourceState={setRecruitmentEntryState} />
                <UnsignedIntEntry elementName='actualNumber' fieldLabel='Actual number of participants'
                    startingValue={recruitmentEntryState.actualNumber} setResourceState={setRecruitmentEntryState} />
                <ReferenceEntry
                    elementName='eligibility' fieldLabel='Eligibility Criteria' startCollapsed={startCollapsed}
                    containedResourceDictionary={containedResourceDictionary}
                    referencedResourceTypes={['Group']} enableCreation={true}
                    startingValue={recruitmentEntryState.eligibility} setResourceState={setRecruitmentEntryState} />
                <ReferenceEntry
                    elementName='actualGroup' fieldLabel='Enrollment Group'
                    containedResourceDictionary={containedResourceDictionary}
                    referencedResourceTypes={['Group']} enableCreation={true} startCollapsed={startCollapsed}
                    startingValue={recruitmentEntryState.actualGroup} setResourceState={setRecruitmentEntryState} />
                <MarkdownEntry elementName='description' fieldLabel='Description'
                    startingValue={recruitmentEntryState.description} setResourceState={setRecruitmentEntryState} />
            </div>
        </>
    }
}

const loadOutcomeMeasureReport = async (resultsReportFoi,
    setHasClinicalTrialsGovResultsState, apiToken) => {
    let resultsReport = await getFhirJson(resultsReportFoi, null, apiToken);
    let outcomeMeasuresReportFoi;
    let outcomeMeasuresReport;
    if (resultsReport?.section) {
        for (const section of resultsReport.section) {
            if (section?.code?.text === "OutcomeMeasuresModule" && section.entry?.length > 0 &&
                section.entry[0].reference?.includes("Composition/")
            ) {
                outcomeMeasuresReportFoi = section.entry[0].reference.replace("Composition/", "");
            }
        }
    }
    if (outcomeMeasuresReportFoi) {
        outcomeMeasuresReport = await getFhirJson(outcomeMeasuresReportFoi, null, apiToken);
    }
    if (outcomeMeasuresReport?.contained) {
        setHasClinicalTrialsGovResultsState(prevState => {
            return {
                ...prevState,
                "outcomeMeasuresReportContained": outcomeMeasuresReport.contained
            }
        })
    }
};

const ResearchStudyDisplay = ({ foi }) => {
    const dispatch = useDispatch();
    const navigate = useNavigate();
    const apiToken = useSelector(selectCurrentUserApiToken);
    const editMode = useSelector(state => selectEditMode(state));
    const parentResourceDetail = useSelector(state => selectResourceById(state, foi));
    const resourceJson = parentResourceDetail?.revisedResourceJson;
    const displayTitle = parentResourceDetail?.displayTitle;
    const viewingOlderVersion = useSelector(state => selectViewingOlderVersion(state));
    const olderVersionFhirString = useSelector(state => selectOlderVersionFhirString(state));
    let isSystematicReviewProfile;
    let isM11ResearchStudyProfile;
    let isStudyDesignProfile;
    let isStudyRegistryRecordProfile;
    if (resourceJson.meta?.profile?.length) {
        for (const profile of resourceJson.meta.profile) {
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/systematic-review") {
                isSystematicReviewProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/pharmaceutical-research-protocol/StructureDefinition/m11-research-study-profile" ||
                profile === "http://hl7.org/fhir/uv/clinical-study-protocol/StructureDefinition/m11-research-study-profile"
            ) {
                isM11ResearchStudyProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/study-design" || profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/study-component") {
                isStudyDesignProfile = true;
            }
            if (profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/study-registry-record" ||
                profile === "http://hl7.org/fhir/uv/ebm/StructureDefinition/m11-research-study") {
                isStudyRegistryRecordProfile = true;
            }
        }
    }

    let nctid;
    let ctisid;
    if (resourceJson.identifier) {
        for (let identifierIndex in resourceJson.identifier) {
            let identifier = resourceJson.identifier[identifierIndex];
            if ((identifier.system === "https://clinicaltrials.gov" ||
                identifier.system === "https://clinicaltrials.gov/nctid") && identifier.value) {
                nctid = identifier.value;
            }
            if ((identifier.system === "https://euclinicaltrials.eu/clinicalTrialId" ||
                identifier.system === "https://euclinicaltrials.eu") && identifier.value) {
                ctisid = identifier.value;
            }
        }
    }

    const [researchStudyResourceState, setResearchStudyResourceState] = useState({});
    const [extensionState, setExtensionState] = useState({});
    const [earlyChangeState, setEarlyChangeState] = useState({});
    const [earlyChangesProcessedState, setEarlyChangesProcessedState] = useState({
        studyRegistrationExtensionProcessed: 0,
        primaryPurposeTypeProcessed: 0
    });
    const [createComparativeEvidenceReportModalState, setCreateComparativeEvidenceReportModalState] = useState({
        modalOpen: false
    });
    const [completeObservedComparisonGroupsModalState, setCompleteObservedComparisonGroupsModalState] = useState({
        modalOpen: false
    });
    const [containedResourceDictionary, setContainedResourceDictionary] = useState({});
    const [hasClinicalTrialsGovResultsState, setHasClinicalTrialsGovResultsState] = useState({});

    useEffect(() => {
        dispatch(changeTool("FEvIR®: ResearchStudy " + (editMode ? "Builder" : "Viewer")));
        dispatch(changeBrowserTabTitle("FEvIR ResearchStudy - " + displayTitle));
    }, [displayTitle, editMode]);

    useEffect(() => {
        dispatch(changeTopNavigationElements(["Summary", "Identification", "Status", "Subject", "Protocol",
            "Organizations", "Recruitment", "Variables", "Results"
        ]));
        if (viewingOlderVersion && olderVersionFhirString) {
            try {
                const olderVersionJson = JSON.parse(olderVersionFhirString);
                setResearchStudyResourceState(olderVersionJson);
                let startingExtension = olderVersionJson.extension || [];
                let startingExtensionStateValue = {
                    "otherExtensions": [], "searchStrategy": [], "studyRegistration": [],
                    "includedStudy": [], "excludedStudy": [], "blindedRole": []
                }
                if (Array.isArray(startingExtension) && startingExtension.length > 0) {
                    for (const extension of startingExtension) {
                        switch (extension.url) {
                            case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-search-strategy':
                                startingExtensionStateValue.searchStrategy.push(extension.valueReference);
                                break;
                            case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-systematic-review-eligibility-criteria':
                                startingExtensionStateValue.systematicReviewEligibilityCriteria = extension.valueReference;
                                break;
                            case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-number-of-studies-identified':
                                startingExtensionStateValue.numberOfStudiesIdentified = extension.valueUnsignedInt;
                                break;
                            case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-number-of-studies-included':
                                startingExtensionStateValue.numberOfStudiesIncluded = extension.valueUnsignedInt;
                                break;
                            case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-included-study':
                                startingExtensionStateValue.includedStudy.push(extension.valueReference);
                                break;
                            case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-excluded-study':
                                startingExtensionStateValue.excludedStudy.push(extension.valueReference);
                                break;
                            case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-sponsor-confidentiality-statement':
                                startingExtensionStateValue.confidentialityStatement = extension.valueString;
                                break;
                            case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-sae-reporting-method':
                                startingExtensionStateValue.saeReportingMethod = extension.valueString;
                                break;
                            case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-study-amendment':
                                startingExtensionStateValue.studyAmendment = extension;
                                break;
                            case 'http://hl7.org/fhir/StructureDefinition/blinded-role':
                                startingExtensionStateValue.blindedRole.push(extension.valueCodeableConcept);
                                break;
                            case 'http://hl7.org/fhir/StructureDefinition/researchStudy-studyRegistration':
                                startingExtensionStateValue.studyRegistration.push(extension);
                                break;
                            case 'http://hl7.org/fhir/uv/clinical-study-protocol/StructureDefinition/narrative-elements':
                                startingExtensionStateValue.section3Narrative = extension.valueReference;
                                break;
                            default:
                                startingExtensionStateValue.otherExtensions.push(extension)
                        }
                    }
                }
                setExtensionState(startingExtensionStateValue);
            } catch {
                alert("Error viewing JSON of older version.");
            }
        } else if (resourceJson) {
            let jsonCopy = JSON.parse(JSON.stringify(resourceJson));
            let primaryPurposeTypeChanged = false;
            if (resourceJson.primaryPurposeType) {
                jsonCopy.purposeType = [JSON.parse(JSON.stringify(resourceJson.primaryPurposeType))];
                delete jsonCopy.primaryPurposeType;
                primaryPurposeTypeChanged = true;
            }
            let startingExtension = jsonCopy.extension || [];
            let studyRegistrationExtensionConverted = false;
            let startingExtensionStateValue = {
                "otherExtensions": [], "searchStrategy": [], "studyRegistration": [],
                "includedStudy": [], "excludedStudy": [], "blindedRole": []
            }
            if (Array.isArray(resourceJson.contained) && resourceJson.contained.length > 0) {
                let containedResourceDictionary = {};
                for (const item of resourceJson.contained) {
                    const key = "#" + item.id;
                    containedResourceDictionary[key] = item;
                }
                setContainedResourceDictionary(containedResourceDictionary);
            }
            if (Array.isArray(startingExtension) && startingExtension.length > 0) {
                let newExtension = [];
                for (const extension of startingExtension) {
                    switch (extension.url) {
                        case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-search-strategy':
                            startingExtensionStateValue.searchStrategy.push(extension.valueReference);
                            newExtension.push(extension);
                            break;
                        case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-systematic-review-eligibility-criteria':
                            startingExtensionStateValue.systematicReviewEligibilityCriteria = extension.valueReference;
                            newExtension.push(extension);
                            break;
                        case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-number-of-studies-identified':
                            startingExtensionStateValue.numberOfStudiesIdentified = extension.valueUnsignedInt;
                            newExtension.push(extension);
                            break;
                        case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-number-of-studies-included':
                            startingExtensionStateValue.numberOfStudiesIncluded = extension.valueUnsignedInt;
                            newExtension.push(extension);
                            break;
                        case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-included-study':
                            startingExtensionStateValue.includedStudy.push(extension.valueReference);
                            newExtension.push(extension);
                            break;
                        case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-excluded-study':
                            startingExtensionStateValue.excludedStudy.push(extension.valueReference);
                            newExtension.push(extension);
                            break;
                        case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-sponsor-confidentiality-statement':
                            startingExtensionStateValue.confidentialityStatement = extension.valueString;
                            newExtension.push(extension);
                            break;
                        case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-sae-reporting-method':
                            startingExtensionStateValue.saeReportingMethod = extension.valueString;
                            newExtension.push(extension);
                            break;
                        case 'http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-study-amendment':
                            startingExtensionStateValue.studyAmendment = extension;
                            newExtension.push(extension);
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/blinded-role':
                            startingExtensionStateValue.blindedRole.push(extension.valueCodeableConcept);
                            newExtension.push(extension);
                            break;
                        case 'http://hl7.org/fhir/StructureDefinition/researchStudy-studyRegistration':
                            startingExtensionStateValue.studyRegistration.push(extension);
                            break;
                        case 'http://hl7.org/fhir/uv/clinical-study-protocol/StructureDefinition/narrative-elements':
                            startingExtensionStateValue.section3Narrative = extension.valueReference;
                            newExtension.push(extension);
                            break;
                        default:
                            startingExtensionStateValue.otherExtensions.push(extension);
                            newExtension.push(extension);
                    }
                }
                jsonCopy.extension = newExtension;
            }
            let progressStatusAdditions = [];
            if (startingExtensionStateValue.studyRegistration?.length > 0) {
                studyRegistrationExtensionConverted = true;
                for (const instance of startingExtensionStateValue.studyRegistration) {
                    let progressStatusAddition = {};
                    let extension = instance.extension;
                    if (extension?.length) {
                        for (let ext of extension) {
                            if (ext.url === "activity") {
                                progressStatusAddition.state = ext.valueCodeableConcept;
                            }
                            if (ext.url === "actual") {
                                progressStatusAddition.actual = ext.valueBoolean;
                            }
                            if (ext.url === "period") {
                                progressStatusAddition.period = ext.valuePeriod;
                            }
                        }
                        progressStatusAdditions.push(progressStatusAddition);
                    }
                }
            }
            if (studyRegistrationExtensionConverted && progressStatusAdditions.length > 0) {
                let startingArray = jsonCopy.progressStatus || [];
                jsonCopy.progressStatus = startingArray.concat(progressStatusAdditions);
            }
            if (resourceJson.result) {
                for (const result of resourceJson.result) {
                    if (result.display?.includes("Results Section for NCT") &&
                        result.reference?.includes("Composition/")) {
                        setHasClinicalTrialsGovResultsState(prevState => {
                            return {
                                ...prevState,
                                "resultsReportFoi": result.reference.replace("Composition/", "")
                            };
                        });
                    }
                }
            }
            setEarlyChangeState({
                studyRegistrationExtensionConverted: studyRegistrationExtensionConverted,
                primaryPurposeTypeChanged: primaryPurposeTypeChanged
            })
            setResearchStudyResourceState(jsonCopy);
            setExtensionState(startingExtensionStateValue);
        }
    }, [resourceJson]);

    useEffect(() => {
        if (hasClinicalTrialsGovResultsState.resultsReportFoi) {
            loadOutcomeMeasureReport(hasClinicalTrialsGovResultsState.resultsReportFoi,
                setHasClinicalTrialsGovResultsState, apiToken);
        }
    }, [hasClinicalTrialsGovResultsState.resultsReportFoi]);

    useEffect(() => {
        if (editMode && resourceJson && researchStudyResourceState) {
            let studyRegistrationExtensionProcessed = 0;
            let primaryPurposeTypeProcessed = 0;
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            if (earlyChangeState.studyRegistrationExtensionConverted &&
                earlyChangesProcessedState.studyRegistrationExtensionProcessed !== 1 &&
                researchStudyResourceState.extension && researchStudyResourceState.progressStatus
            ) {
                console.log("resourceUpdate for Resource.extension");
                newResourceDetail.revisedResourceJson.extension = JSON.parse(JSON.stringify(researchStudyResourceState.extension))
                console.log("resourceUpdate for Resource.progressStatus");
                newResourceDetail.revisedResourceJson.progressStatus = JSON.parse(JSON.stringify(researchStudyResourceState.progressStatus))
                studyRegistrationExtensionProcessed = 1;
            }
            if (earlyChangeState.primaryPurposeTypeChanged &&
                earlyChangesProcessedState.primaryPurposeTypeProcessed !== 1 &&
                researchStudyResourceState.purposeType
            ) {
                console.log("resourceUpdate for Resource.purposeType");
                newResourceDetail.revisedResourceJson.purposeType = JSON.parse(JSON.stringify(researchStudyResourceState.purposeType));
                if (newResourceDetail.revisedResourceJson.primaryPurposeType) {
                    delete newResourceDetail.revisedResourceJson.primaryPurposeType;
                }
                primaryPurposeTypeProcessed = 1;
            }
            if (primaryPurposeTypeProcessed === 1 || studyRegistrationExtensionProcessed === 1) {
                dispatch(resourceUpdated(newResourceDetail));
            }
            setEarlyChangesProcessedState(prevState => {
                return {
                    ...prevState,
                    studyRegistrationExtensionProcessed: prevState.studyRegistrationExtensionProcessed + studyRegistrationExtensionProcessed,
                    primaryPurposeTypeProcessed: prevState.primaryPurposeTypeProcessed + primaryPurposeTypeProcessed
                }
            })
        }
    }, [earlyChangeState, editMode]);

    useEffect(() => {
        let newExtension = [];
        if (extensionState.otherExtensions) {
            newExtension = JSON.parse(JSON.stringify(extensionState.otherExtensions));
        }
        if (extensionState.searchStrategy?.length > 0) {
            for (const instance of extensionState.searchStrategy) {
                newExtension.push({
                    "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-search-strategy",
                    "valueReference": instance
                });
            }
        }
        if (extensionState.systematicReviewEligibilityCriteria) {
            newExtension.push({
                "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-systematic-review-eligibility-criteria",
                "valueReference": extensionState.systematicReviewEligibilityCriteria
            });
        }
        if (!isNaN(extensionState.numberOfStudiesIdentified)) {
            newExtension.push({
                "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-number-of-studies-identified",
                "valueUnsignedInt": extensionState.numberOfStudiesIdentified
            });
        }
        if (!isNaN(extensionState.numberOfStudiesIncluded) || extensionState.numberOfStudiesIncluded === "") {
            newExtension.push({
                "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-number-of-studies-included",
                "valueUnsignedInt": extensionState.numberOfStudiesIncluded
            });
        }
        if (extensionState.includedStudy?.length > 0) {
            for (const instance of extensionState.includedStudy) {
                newExtension.push({
                    "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-included-study",
                    "valueReference": instance
                });
            }
        }
        if (extensionState.excludedStudy?.length > 0) {
            for (const instance of extensionState.excludedStudy) {
                newExtension.push({
                    "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-excluded-study",
                    "valueReference": instance
                });
            }
        }
        if (extensionState.blindedRole?.length > 0) {
            for (const instance of extensionState.blindedRole) {
                newExtension.push({
                    "url": "http://hl7.org/fhir/StructureDefinition/blinded-role",
                    "valueCodeableConcept": instance
                });
            }
        }
        if (extensionState.confidentialityStatement || extensionState.confidentialityStatement === "") {
            newExtension.push({
                "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-sponsor-confidentiality-statement",
                "valueString": extensionState.confidentialityStatement
            });
        }
        if (extensionState.saeReportingMethod || extensionState.saeReportingMethod === "") {
            newExtension.push({
                "url": "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-sae-reporting-method",
                "valueString": extensionState.saeReportingMethod
            });
        }
        if (extensionState.section3Narrative) {
            newExtension.push({
                "url": "http://hl7.org/fhir/uv/clinical-study-protocol/StructureDefinition/narrative-elements",
                "valueReference": extensionState.section3Narrative
            });
        }
        if (extensionState.studyAmendment && Object.keys(extensionState.studyAmendment).length > 0) {
            newExtension.push(extensionState.studyAmendment);
        }
        setResearchStudyResourceState(prevState => { return { ...prevState, "extension": newExtension } });
    }, [extensionState]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateNarrativeElement(researchStudyResourceState, "text", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.text]);

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousFinalValue = newResourceDetail.revisedResourceJson.title || null;
            newResourceDetail.revisedResourceJson.title = researchStudyResourceState.title;
            newResourceDetail.displayTitle = researchStudyResourceState.title ||
                researchStudyResourceState.name || newResourceDetail.displayTitle;
            if (researchStudyResourceState.title !== undefined &&
                previousFinalValue !== researchStudyResourceState.title) {
                console.log("resourceUpdate for ResearchStudy.title");
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [researchStudyResourceState.title]);

    useEffect(() => {
        if (editMode && resourceJson) {
            let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
            let previousFinalValue = newResourceDetail.revisedResourceJson.name || null;
            newResourceDetail.revisedResourceJson.name = researchStudyResourceState.name;
            newResourceDetail.displayTitle = researchStudyResourceState.title ||
                researchStudyResourceState.name || newResourceDetail.displayTitle;
            if (researchStudyResourceState.name !== undefined &&
                previousFinalValue !== researchStudyResourceState.name) {
                console.log("resourceUpdate for ResearchStudy.name");
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [researchStudyResourceState.name]);

    useEffect(() => {
        if (editMode && resourceJson) {
            if (earlyChangesProcessedState.studyRegistrationExtensionProcessed === 1) {
                //no action
            } else {
                updateElement(researchStudyResourceState, "extension", "ArrayOfExtension", parentResourceDetail,
                    dispatch);
            }
        }
    }, [researchStudyResourceState.extension]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "label", "ArrayOfResearchStudyLabel", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.label]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "identifier", "ArrayOfIdentifier", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.identifier]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "version", "string", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.version]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "partOf", "ArrayOfReference", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.partOf]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "period", "Period", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.period]);

    useEffect(() => {
        if (editMode && resourceJson) {
            if (earlyChangesProcessedState.studyRegistrationExtensionProcessed === 1) {
                if (!earlyChangesProcessedState.alertShown) {
                    alert("Study Registration status extensions were converted to progressStatus elements. Save All Changes and Edit ResearchStudy to continue editing.")
                } else {
                    setEarlyChangesProcessedState(prevState => { return { ...prevState, alertShown: true } });
                }
            } else {
                updateElement(researchStudyResourceState, "progressStatus", "ArrayOfResearchStudyProgressStatus", parentResourceDetail,
                    dispatch);
            }
        }
    }, [researchStudyResourceState.progressStatus]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "whyStopped", "CodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.whyStopped]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "descriptionSummary", "markdown", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.descriptionSummary]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "description", "markdown", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.description]);

    useEffect(() => {
        if (editMode && resourceJson) {
            const previousInitialValue = parentResourceDetail.resourceJson.purposeType; // ?? null
            const previousFinalValue = parentResourceDetail.revisedResourceJson.purposeType; // ?? null
            if (researchStudyResourceState.purposeType !== undefined
                && (previousInitialValue || previousFinalValue || researchStudyResourceState.purposeType)
                && (previousInitialValue || researchStudyResourceState.purposeType?.length > 1 || researchStudyResourceState.purposeType?.[0])
                && (!jsonEquals(previousFinalValue, researchStudyResourceState.purposeType))
            ) {
                console.log("resourceUpdate for Resource.purposeType");
                let newResourceDetail = JSON.parse(JSON.stringify(parentResourceDetail));
                newResourceDetail.revisedResourceJson.purposeType = JSON.parse(JSON.stringify(researchStudyResourceState.purposeType));
                if (newResourceDetail.revisedResourceJson.primaryPurposeType) {
                    delete newResourceDetail.revisedResourceJson.primaryPurposeType;
                }
                dispatch(resourceUpdated(newResourceDetail));
            }
        }
    }, [researchStudyResourceState.purposeType]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "focus", "ArrayOfCodeableReference", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.focus]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "condition", "ArrayOfCodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.condition]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "keyword", "ArrayOfCodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.keyword]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "classifier", "ArrayOfCodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.classifier]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "region", "ArrayOfCodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.region]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "phase", "CodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.phase]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "studyDesign", "ArrayOfCodeableConcept", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.studyDesign]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "protocol", "ArrayOfReference", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.protocol]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "relatesTo", "ArrayOfRelatesTo", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.relatesTo]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "note", "ArrayOfAnnotation", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.note]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "associatedParty", "ArrayOfResearchStudyAssociatedParty", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.associatedParty]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "site", "ArrayOfReference", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.site]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "recruitment", "ResearchStudyRecruitment", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.recruitment]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "comparisonGroup", "ArrayOfResearchStudyComparisonGroup", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.comparisonGroup]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "objective", "ArrayOfResearchStudyObjective", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.objective]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "outcomeMeasure", "ArrayOfResearchStudyOutcomeMeasure", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.outcomeMeasure]);

    useEffect(() => {
        if (editMode && resourceJson) {
            updateElement(researchStudyResourceState, "result", "ArrayOfReference", parentResourceDetail,
                dispatch);
        }
    }, [researchStudyResourceState.result]);

    if (!resourceJson) {
        return <Spinner />;
    }

    return <div>
        {researchStudyResourceState.id && <>
            <div>
                {createComparativeEvidenceReportModalState.modalOpen &&
                    <CreateComparativeEvidenceReportModal researchStudyResourceState={researchStudyResourceState}
                        containedResourceDictionary={containedResourceDictionary}
                        createComparativeEvidenceReportModalState={createComparativeEvidenceReportModalState}
                        setCreateComparativeEvidenceReportModalState={setCreateComparativeEvidenceReportModalState} />}
                {completeObservedComparisonGroupsModalState.modalOpen &&
                    <CompleteObservedComparisonGroupsModal researchStudyResourceState={researchStudyResourceState}
                        completeObservedComparisonGroupsModalState={completeObservedComparisonGroupsModalState}
                        setCompleteObservedComparisonGroupsModalState={setCompleteObservedComparisonGroupsModalState}
                        setResearchStudyResourceState={setResearchStudyResourceState}
                        outcomeMeasuresReportContained={hasClinicalTrialsGovResultsState.outcomeMeasuresReportContained}
                    />}
                {nctid && <div style={{ width: "100%", textAlign: "center" }}>
                    <b>VIEW on ClinicalTrials.gov: <a href={"https://clinicaltrials.gov/study/" + nctid}
                        target="_blank" rel="noopener noreferrer">{nctid}</a></b>
                </div>}
                {(nctid && ctisid) && <><br /></>}
                {ctisid && <div style={{ width: "100%", textAlign: "center" }}>
                    <b>VIEW on EU CTIS: <a href={"https://euclinicaltrials.eu/search-for-clinical-trials/?lang=en&EUCT=" + ctisid}
                        target="_blank" rel="noopener noreferrer">{ctisid}</a></b>
                </div>}
                <div style={{ marginTop: "12px" }}>
                    <h3 id="summary">Summary</h3>
                    <div style={{ marginLeft: "24px" }}>
                        {(researchStudyResourceState.comparisonGroup?.length > 1 && apiToken) &&
                            <>
                                <Button className="formButton" style={{ color: "#000000" }}
                                    content="Create Comparative Evidence Report"
                                    onClick={async () => {
                                        setCreateComparativeEvidenceReportModalState(prevState => { return { ...prevState, modalOpen: true } });
                                    }} />
                                <br />
                            </>}
                        {(!isM11ResearchStudyProfile && apiToken) &&
                            <>
                                <Button className="formButton" style={{ color: "#000000" }}
                                    content="Create M11ResearchStudy"
                                    onClick={async () => {
                                        createM11ResearchStudy(researchStudyResourceState, apiToken, navigate, dispatch);
                                    }} />
                                <br />
                            </>}
                        {
                            (isM11ResearchStudyProfile && editMode) &&
                            <>
                                <Button className="formButton" style={{ color: "#000000" }}
                                    content="Create M11 Report"
                                    onClick={async () => {
                                        createM11ReportComposition(researchStudyResourceState, apiToken, extensionState, setResearchStudyResourceState);
                                    }} />
                                <br />
                            </>
                        }
                        {
                            editMode ?
                                <>
                                    <NarrativeEntry elementName='text' fieldLabel='Narrative Summary'
                                        generatedNarrative={generateNarrativeForResearchStudyResource(researchStudyResourceState)} startCollapsed
                                        startingValue={researchStudyResourceState.text} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {(researchStudyResourceState.text?.div && researchStudyResourceState.text.status && researchStudyResourceState.text.status !== "empty") ?
                                        <DisplayFromFHIRXhtml xhtml={researchStudyResourceState.text.div} />
                                        : <p>[Narrative summary not generated.]</p>}
                                </>
                        }
                    </div>
                    <h3 id="identification">Identification</h3>
                    <div style={{ marginLeft: "24px" }}>
                        {
                            editMode ?
                                <>
                                    <StringEntry elementName='title' fieldLabel='Title'
                                        startingValue={researchStudyResourceState.title} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {researchStudyResourceState.title && <p><b>Title: </b>{researchStudyResourceState.title}</p>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <StringEntry elementName='name' fieldLabel='Name'
                                        startingValue={researchStudyResourceState.name} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {researchStudyResourceState.name && <p><b>Name: </b>{researchStudyResourceState.name}</p>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ResearchStudyLabelTableEntry startingValue={researchStudyResourceState.label}
                                        setResourceState={setResearchStudyResourceState} startCollapsed={true} />
                                    {/**      <ArrayEntry datatype='ResearchStudyLabel' elementName='label' fieldLabel='Label'
                                        startCollapsed startEmptyArrayClosed={true}
                                        startingValue={researchStudyResourceState.label} setResourceState={setResearchStudyResourceState} />
                            */}
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.label) &&
                                        researchStudyResourceState.label.map((label, labelIndex) => {
                                            let labelType = "Label";
                                            if (label.type) {
                                                labelType = simpleDisplayStringFromFHIRCodeableConcept(label.type);
                                            }
                                            if (label.language) {
                                                return <p key={labelIndex}>
                                                    <b>{labelType}, Language -- {languageCodeDisplay[label.language]}: </b>{label.value}
                                                </p>
                                            } else {
                                                return <p key={labelIndex}><b>{labelType}: </b>{label.value}</p>
                                            }
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='Identifier' elementName='identifier' fieldLabel='Identifier'
                                        startCollapsed startEmptyArrayClosed={true} containedResourceDictionary={containedResourceDictionary}
                                        startingValue={researchStudyResourceState.identifier} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.identifier) &&
                                        researchStudyResourceState.identifier.map((identifier, identifierIndex) => {
                                            return <div key={identifierIndex}><b>Identifier: </b>{displayStringFromFHIRIdentifier(identifier)}</div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <StringEntry elementName='version' fieldLabel='Version'
                                        startingValue={researchStudyResourceState.version} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {researchStudyResourceState.version && <p><b>Version: </b>{researchStudyResourceState.version}</p>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='Reference' elementName='partOf'
                                        fieldLabel='Part of (ResearchStudy)'
                                        referencedResourceTypes={['ResearchStudy']} enableCreation={true}
                                        startCollapsed startEmptyArrayClosed={true}
                                        startingValue={researchStudyResourceState.partOf || null}
                                        setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.partOf) &&
                                        researchStudyResourceState.partOf.map((partOf, partOfIndex) => {
                                            return <div key={partOfIndex}><b>Part of: </b><DisplayFromFHIRReference reference={partOf}
                                                containedResourceJson={partOf.reference && containedResourceDictionary[partOf.reference]} /></div>
                                        })}
                                </>
                        }
                    </div>
                    <h3 id="status">Status</h3>
                    <div style={{ marginLeft: "24px" }}>
                        {
                            editMode ?
                                <>
                                    {(isM11ResearchStudyProfile || isStudyDesignProfile) && <StringEntry
                                        elementName='confidentialityStatement'
                                        fieldLabel='Sponsor Confidentiality Statement'
                                        startingValue={extensionState.confidentialityStatement}
                                        setResourceState={setExtensionState} />}
                                </>
                                :
                                <>
                                    {extensionState.confidentialityStatement &&
                                        <p><b>Sponsor Confidentiality Statement: </b>{extensionState.confidentialityStatement}</p>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <PeriodEntry elementName='period' fieldLabel='Study start and end'
                                        startCollapsed
                                        startingValue={researchStudyResourceState.period} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {researchStudyResourceState.period &&
                                        <p><b>Study start and end: </b>{displayStringFromFHIRPeriod(researchStudyResourceState.period)}</p>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='ResearchStudyProgressStatus'
                                        elementName='progressStatus' fieldLabel='Progress Status'
                                        startCollapsed startEmptyArrayClosed={true}
                                        startingValue={researchStudyResourceState.progressStatus}
                                        setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.progressStatus) &&
                                        researchStudyResourceState.progressStatus.map((progress, progressIndex) => {
                                            return <div key={progressIndex}><b>Progress Status: </b>
                                                <div style={{ marginLeft: "24px" }}>
                                                    {progress.state && <span><b>State: </b><DisplayFromFHIRCodeableConcept codeableConcept={progress.state} /><br /></span>}
                                                    {progress.actual === true && <p><b>Actual</b></p>}
                                                    {progress.actual === false && <p><b>Anticipated</b></p>}
                                                    {progress.period && <p><b>Period: </b>{displayStringFromFHIRPeriod(progress.period)}</p>}
                                                </div>
                                            </div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <CodeableConceptEntry elementName='whyStopped' fieldLabel='Why stopped'
                                        valueSet={researchStudyDotWhyStoppedValueSet} startCollapsed
                                        startingValue={researchStudyResourceState.whyStopped} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {researchStudyResourceState.whyStopped &&
                                        <p><b>Why stopped: </b><DisplayFromFHIRCodeableConcept codeableConcept={researchStudyResourceState.whyStopped} /></p>}
                                </>
                        }
                    </div>
                    <h3 id="subject">Subject</h3>
                    <div style={{ marginLeft: "24px" }}>
                        {
                            editMode ?
                                <>
                                    <MarkdownEntry elementName='descriptionSummary' fieldLabel='Brief Description'
                                        startCollapsed
                                        startingValue={researchStudyResourceState.descriptionSummary} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {researchStudyResourceState.descriptionSummary &&
                                        <div>
                                            <b>Brief Description: </b><DisplayFromFHIRMarkdown markdown={researchStudyResourceState.descriptionSummary} />
                                            <br /><br />
                                        </div>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <MarkdownEntry elementName='description' fieldLabel='Full Description'
                                        startCollapsed
                                        startingValue={researchStudyResourceState.description} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {researchStudyResourceState.description &&
                                        <div><b>Description: </b><DisplayFromFHIRMarkdown markdown={researchStudyResourceState.description} /><br /><br /></div>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='CodeableConcept'
                                        elementName='purposeType' fieldLabel='Purpose Type'
                                        deletable deletableArray
                                        valueSet={researchStudyDotPurposeTypeValueSet}
                                        codeableConceptLevelValueSet={researchStudyDotPurposeCodeableConceptSet}
                                        startCollapsed startEmptyArrayClosed={true}
                                        startingValue={researchStudyResourceState.purposeType}
                                        setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {researchStudyResourceState.primaryPurposeType &&
                                        <span><b>Purpose Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={researchStudyResourceState.primaryPurposeType} /><br /></span>}
                                    {Array.isArray(researchStudyResourceState.purposeType) &&
                                        researchStudyResourceState.purposeType.map((purpose, purposeIndex) => {
                                            return <div key={purposeIndex}>
                                                <b>Purpose Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={purpose} />
                                            </div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='CodeableReference'
                                        containedResourceDictionary={containedResourceDictionary}
                                        elementName='focus' fieldLabel='Focus'
                                        startCollapsed deletable deletableArray startEmptyArrayClosed={true}
                                        referencedResourceTypes={focusReferenceResourceTypes}
                                        startingValue={researchStudyResourceState.focus} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.focus) &&
                                        researchStudyResourceState.focus.map((focus, focusIndex) => {
                                            return <div key={focusIndex}><b>Focus: </b><DisplayFromFHIRCodeableReference codeableReference={focus}
                                                containedResourceDictionary={containedResourceDictionary} /></div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='CodeableConcept' deletable={true}
                                        elementName='condition' fieldLabel='Condition'
                                        startCollapsed startEmptyArrayClosed={true}
                                        startingValue={researchStudyResourceState.condition} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.condition) &&
                                        researchStudyResourceState.condition.map((condition, conditionIndex) => {
                                            return <div key={conditionIndex}><b>Condition: </b><DisplayFromFHIRCodeableConcept codeableConcept={condition} /></div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='CodeableConcept' deletable={true}
                                        elementName='keyword' fieldLabel='Keyword'
                                        startCollapsed startEmptyArrayClosed={true}
                                        startingValue={researchStudyResourceState.keyword} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.keyword) &&
                                        researchStudyResourceState.keyword.map((keyword, keywordIndex) => {
                                            return <div key={keywordIndex}><b>Keyword: </b><DisplayFromFHIRCodeableConcept codeableConcept={keyword} /></div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='CodeableConcept' deletable={true}
                                        elementName='classifier' fieldLabel='Classifier'
                                        startCollapsed startEmptyArrayClosed={true}
                                        startingValue={researchStudyResourceState.classifier} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.classifier) &&
                                        researchStudyResourceState.classifier.map((classifier, classifierIndex) => {
                                            return <div key={classifierIndex}><b>Classifier: </b><DisplayFromFHIRCodeableConcept codeableConcept={classifier} /></div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='CodeableConcept'
                                        elementName='region' fieldLabel='Region'
                                        startCollapsed startEmptyArrayClosed={true}
                                        startingValue={researchStudyResourceState.region} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.region) &&
                                        researchStudyResourceState.region.map((region, regionIndex) => {
                                            return <div key={regionIndex}><b>Region: </b><DisplayFromFHIRCodeableConcept codeableConcept={region} /></div>
                                        })}
                                </>
                        }
                    </div>
                    <h3 id="protocol">Protocol</h3>
                    <div style={{ marginLeft: "24px" }}>
                        {
                            editMode ?
                                <>
                                    {(isM11ResearchStudyProfile || isStudyDesignProfile) && <StudyAmendmentEntry
                                        elementName='studyAmendment'
                                        fieldLabel='Study Amendment'
                                        startingValue={extensionState.studyAmendment} startCollapsed
                                        setResourceState={setExtensionState} />}
                                </>
                                :
                                <>
                                    {extensionState.studyAmendment && <div>
                                        <h4>Study Amendment</h4>
                                        <div style={{ marginLeft: "24px" }}>
                                            {extensionState.studyAmendment.details && <p><b>Details: </b>{extensionState.studyAmendment.details}</p>}
                                            {extensionState.studyAmendment.scope && <p><b>Scope: </b>{extensionState.studyAmendment.scope}</p>}
                                            {extensionState.studyAmendment.description && <p><b>Description: </b>{extensionState.studyAmendment.description}</p>}
                                            {extensionState.studyAmendment.rationale && <p><b>Rationale: </b>{extensionState.studyAmendment.rationale}</p>}
                                            {extensionState.studyAmendment.substantialImpactSafety === true && <p><b>Substantial Impact on Safety: </b>YES</p>}
                                            {extensionState.studyAmendment.substantialImpactSafety === false && <p><b>Substantial Impact on Safety: </b>No</p>}
                                            {extensionState.studyAmendment.substantialImpactSafetyComment && <p><b>Substantial Impact on Safety Comment: </b>{extensionState.studyAmendment.substantialImpactSafetyComment}</p>}
                                            {extensionState.studyAmendment.substantialImpactReliability === true && <p><b>Substantial Impact on Reliability: </b>YES</p>}
                                            {extensionState.studyAmendment.substantialImpactReliability === false && <p><b>Substantial Impact on Reliability: </b>No</p>}
                                            {extensionState.studyAmendment.substantialImpactReliabilityComment && <p><b>Substantial Impact on Reliability Comment: </b>{extensionState.studyAmendment.substantialImpactReliabilityComment}</p>}
                                            {extensionState.studyAmendment.primaryReason && <p><b>Primary Reason: </b>{displayStringFromFHIRCodeableConcept(extensionState.studyAmendment.primaryReason)}</p>}
                                        </div>
                                    </div>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    {isSystematicReviewProfile && <>
                                        <ReferenceEntry
                                            elementName='systematicReviewEligibilityCriteria'
                                            containedResourceDictionary={containedResourceDictionary}
                                            fieldLabel='Systematic Review Eligibility Criteria'
                                            enableCreation startCollapsed startingResourceType="Group"
                                            referencedResourceTypes={['Group']} setProfile="SystematicReviewEligibilityCriteria"
                                            startingValue={extensionState.systematicReviewEligibilityCriteria}
                                            setResourceState={setExtensionState} />
                                    </>}
                                </>
                                :
                                <>
                                    {extensionState.systematicReviewEligibilityCriteria && <div>
                                        <b>Systematic Review Eligibility Criteria: </b><DisplayFromFHIRReference reference={extensionState.systematicReviewEligibilityCriteria}
                                            containedResourceJson={extensionState.systematicReviewEligibilityCriteria.reference && containedResourceDictionary[extensionState.systematicReviewEligibilityCriteria.reference]} /></div>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    {isSystematicReviewProfile && <>
                                        <ArrayEntry datatype='Reference'
                                            elementName='searchStrategy'
                                            fieldLabel='Search Strategy'
                                            enableCreation startCollapsed startEmptyArrayClosed={true}
                                            startingResourceType="Group"
                                            referencedResourceTypes={['Group']} setProfile="SearchStrategy"
                                            startingValue={extensionState.searchStrategy}
                                            setResourceState={setExtensionState} />
                                    </>}
                                </>
                                :
                                <>
                                    {Array.isArray(extensionState.searchStrategy) &&
                                        extensionState.searchStrategy.map((protocol, protocolIndex) => {
                                            return <div key={protocolIndex}><b>Search Strategy: </b><DisplayFromFHIRReference reference={protocol}
                                                containedResourceJson={protocol.reference && containedResourceDictionary[protocol.reference]} /></div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    {!isSystematicReviewProfile && <CodeableConceptEntry elementName='phase' fieldLabel='Phase'
                                        valueSet={researchStudyDotPhaseValueSet} startCollapsed
                                        codeableConceptLevelValueSet={researchStudyPhaseCodeableConceptSet}
                                        startingValue={researchStudyResourceState.phase} setResourceState={setResearchStudyResourceState} />}
                                </>
                                :
                                <>
                                    {researchStudyResourceState.phase &&
                                        <span><b>Phase: </b><DisplayFromFHIRCodeableConcept codeableConcept={researchStudyResourceState.phase} /><br /></span>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='CodeableConcept'
                                        elementName='studyDesign' fieldLabel='Study Design'
                                        valueSet={SEVCO.studyDesign}
                                        startCollapsed startEmptyArrayClosed={true}
                                        codeableConceptLevelValueSet={studyDesignCodeableConceptSet}
                                        startingValue={researchStudyResourceState.studyDesign}
                                        setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.studyDesign) &&
                                        researchStudyResourceState.studyDesign.map((studyDesign, studyDesignIndex) => {
                                            return <div key={studyDesignIndex}><b>Study Design: </b><DisplayFromFHIRCodeableConcept codeableConcept={studyDesign} /></div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='CodeableConcept'
                                        elementName='blindedRole' fieldLabel='Blinded Role'
                                        startCollapsed startEmptyArrayClosed={true}
                                        codeableConceptLevelValueSet={blindedRoleCodeableConceptSet}
                                        startingValue={extensionState.blindedRole}
                                        setResourceState={setExtensionState} />
                                </>
                                :
                                <>
                                    {Array.isArray(extensionState.blindedRole) &&
                                        extensionState.blindedRole.map((blindedRole, blindedRoleIndex) => {
                                            return <div key={blindedRoleIndex}><b>Blinded Role: </b><DisplayFromFHIRCodeableConcept codeableConcept={blindedRole} /></div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='Reference'
                                        elementName='protocol' fieldLabel='Protocol'
                                        startCollapsed startEmptyArrayClosed={true}
                                        referencedResourceTypes={['PlanDefinition']}
                                        startingValue={researchStudyResourceState.protocol} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.protocol) &&
                                        researchStudyResourceState.protocol.map((protocol, protocolIndex) => {
                                            return <div key={protocolIndex}><b>Protocol: </b><DisplayFromFHIRReference reference={protocol}
                                                containedResourceJson={protocol.reference && containedResourceDictionary[protocol.reference]} /></div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    {(isM11ResearchStudyProfile || isStudyDesignProfile) && <StringEntry
                                        elementName='saeReportingMethod'
                                        fieldLabel='Serious Adverse Event Reporting Method'
                                        startingValue={extensionState.saeReportingMethod}
                                        setResourceState={setExtensionState} />}
                                </>
                                :
                                <>
                                    {extensionState.saeReportingMethod &&
                                        <p><b>Serious Adverse Event Reporting Method: </b>{extensionState.saeReportingMethod}</p>}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='RelatesTo' startCollapsed startEmptyArrayClosed={true}
                                        containedResourceDictionary={containedResourceDictionary}
                                        elementName='relatesTo' fieldLabel='Related Item' allowedTypeValues="FHIR"
                                        startingValue={researchStudyResourceState.relatesTo} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.relatedArtifact) &&
                                        researchStudyResourceState.relatedArtifact.map((relatedArtifact, relatedArtifactIndex) => {
                                            return <div key={relatedArtifactIndex}><b>Related Item: </b><DisplayFromFHIRRelatedArtifact relatedArtifact={relatedArtifact}
                                                containedResourceDictionary={containedResourceDictionary} /></div>
                                        })}
                                    {Array.isArray(researchStudyResourceState.relatesTo) &&
                                        researchStudyResourceState.relatesTo.map((relatedArtifact, relatedArtifactIndex) => {
                                            return <div key={relatedArtifactIndex}><b>Related Item: </b><DisplayFromFHIRRelatesTo relatesTo={relatedArtifact}
                                                containedResourceDictionary={containedResourceDictionary} /></div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='Annotation'
                                        elementName='note' fieldLabel='Note'
                                        startCollapsed startEmptyArrayClosed={true}
                                        startingValue={researchStudyResourceState.note}
                                        setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.note) &&
                                        researchStudyResourceState.note.map((note, noteIndex) => {
                                            return <div key={noteIndex}><b>Note: </b><DisplayFromFHIRAnnotation annotation={note} /></div>
                                        })}
                                </>
                        }
                    </div>
                    <h3 id="organizations">Organizations</h3>
                    <div style={{ marginLeft: "24px" }}>
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='ResearchStudyAssociatedParty'
                                        containedResourceDictionary={containedResourceDictionary}
                                        elementName='associatedParty' fieldLabel='Associated Party'
                                        startCollapsed startEmptyArrayClosed={true}
                                        startingValue={researchStudyResourceState.associatedParty}
                                        setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.associatedParty) &&
                                        researchStudyResourceState.associatedParty.map((party, partyIndex) => {
                                            return <div key={partyIndex}><b>Associated Party: </b>
                                                <div style={{ marginLeft: "24px" }}>
                                                    {party.name && <p><b>Name: </b>{party.name}</p>}
                                                    {party.role && <span><b>Role: </b><DisplayFromFHIRCodeableConcept codeableConcept={party.role} /><br /></span>}
                                                    {Array.isArray(party.period) &&
                                                        party.period.map((period, periodIndex) => {
                                                            return <div key={periodIndex}><b>Period: </b>{displayStringFromFHIRPeriod(period)}</div>
                                                        })}
                                                    {Array.isArray(party.classifier) &&
                                                        party.classifier.map((classifier, classifierIndex) => {
                                                            return <div key={classifierIndex}><b>Classifier: </b><DisplayFromFHIRCodeableConcept codeableConcept={classifier} /></div>
                                                        })}
                                                    {party.party && <div><b>Party: </b><DisplayFromFHIRReference reference={party.party}
                                                        containedResourceJson={party.party.reference && containedResourceDictionary[party.party.reference]} /><br /></div>}
                                                </div>
                                            </div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='Reference'
                                        elementName='site' fieldLabel='Site' enableCreation={true}
                                        startCollapsed startEmptyArrayClosed={true}
                                        referencedResourceTypes={siteReferenceResourceTypes}
                                        startingValue={researchStudyResourceState.site} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.site) &&
                                        researchStudyResourceState.site.map((siteInstance, siteIndex) => {
                                            return <div key={siteIndex}><b>Site: </b><DisplayFromFHIRReference reference={siteInstance}
                                                containedResourceJson={siteInstance.reference && containedResourceDictionary[siteInstance.reference]} /></div>
                                        })}
                                </>
                        }
                    </div>
                    <h3 id="recruitment">Recruitment</h3>
                    <div style={{ marginLeft: "24px" }}>
                        {
                            editMode ?
                                <>
                                    {isSystematicReviewProfile ?
                                        <>This section is not relevant for a systematic review.</>
                                        :
                                        <ResearchStudyRecruitmentEntry elementName='recruitment'
                                            fieldLabel='Recruitment' startCollapsed
                                            containedResourceDictionary={containedResourceDictionary}
                                            startingValue={researchStudyResourceState.recruitment} setResourceState={setResearchStudyResourceState} />
                                    }
                                </>
                                :
                                <>
                                    {researchStudyResourceState.recruitment?.targetNumber && <p><b>Target number of participants: </b>{researchStudyResourceState.recruitment.targetNumber}</p>}
                                    {(researchStudyResourceState.recruitment?.actualNumber || researchStudyResourceState.recruitment?.actualNumber === 0) && <p><b>Actual number of participants: </b>{researchStudyResourceState.recruitment.actualNumber}</p>}
                                    {researchStudyResourceState.recruitment?.eligibility && <div><b>Eligibility Criteria: </b><DisplayFromFHIRReference reference={researchStudyResourceState.recruitment.eligibility}
                                        containedResourceJson={researchStudyResourceState.recruitment.eligibility.reference && containedResourceDictionary[researchStudyResourceState.recruitment.eligibility.reference]} /><br /></div>}
                                    {researchStudyResourceState.recruitment?.actualGroup && <div><b>Enrollment Group: </b><DisplayFromFHIRReference reference={researchStudyResourceState.recruitment.actualGroup}
                                        containedResourceJson={researchStudyResourceState.recruitment.actualGroup.reference && containedResourceDictionary[researchStudyResourceState.recruitment.actualGroup.reference]} /><br /></div>}
                                    {researchStudyResourceState.recruitment?.description && <div><b>Description: </b><DisplayFromFHIRMarkdown markdown={researchStudyResourceState.recruitment.description} /></div>}
                                    {!researchStudyResourceState.recruitment && <p>No recruitment data.</p>}
                                </>
                        }
                    </div>
                    <h3 id="variables">Variables</h3>
                    <div style={{ marginLeft: "24px" }}>
                        {extensionState.section3Narrative &&
                            <p><b>Section 3 Narrative: </b>
                                <DisplayFromFHIRReference reference={extensionState.section3Narrative}
                                    containedResourceJson={extensionState.section3Narrative.reference && containedResourceDictionary[extensionState.section3Narrative.reference]} />
                            </p>}
                        {
                            (isM11ResearchStudyProfile && editMode) &&
                            <>
                                <Button className="formButton" style={{ color: "#000000" }}
                                    content="Generate Section 3 Narrative"
                                    onClick={() => {
                                        createM11Section3Composition(researchStudyResourceState, apiToken, setExtensionState);
                                    }} />
                                <br />
                            </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='ResearchStudyComparisonGroup'
                                        containedResourceDictionary={containedResourceDictionary}
                                        elementName='comparisonGroup' fieldLabel='Comparison Group'
                                        startCollapsed startEmptyArrayClosed={true}
                                        startingValue={researchStudyResourceState.comparisonGroup}
                                        setResourceState={setResearchStudyResourceState} />
                                    {hasClinicalTrialsGovResultsState?.outcomeMeasuresReportContained &&
                                        <>
                                            <Button className="formButton" style={{ color: "#000000" }}
                                                content="Add observed comparison groups from Outcome Measures Report"
                                                onClick={async () => {
                                                    setCompleteObservedComparisonGroupsModalState(prevState => { return { ...prevState, modalOpen: true } });
                                                }} />
                                            <br />
                                        </>}
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.comparisonGroup) &&
                                        researchStudyResourceState.comparisonGroup.map((exposure, exposureIndex) => {
                                            return <div key={exposureIndex}><b>Comparison Group: </b>
                                                <div style={{ marginLeft: "24px" }}>
                                                    {exposure.linkId && <p><b>Id: </b>{exposure.linkId}</p>}
                                                    {exposure.name && <p><b>Name: </b>{exposure.name}</p>}
                                                    {exposure.type && <span><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={exposure.type} /><br /></span>}
                                                    {exposure.description && <div><b>Description: </b><DisplayFromFHIRMarkdown markdown={exposure.description} /><br /><br /></div>}
                                                    {exposure.targetNumber && <p><b>Target number of participants: </b>{exposure.targetNumber}</p>}
                                                    {(exposure.actualNumber || exposure.actualNumber === 0) && <p><b>Actual number of participants: </b>{exposure.actualNumber}</p>}
                                                    {exposure.eligibility && <div><b>Eligibility Criteria: </b><DisplayFromFHIRReference reference={exposure.eligibility}
                                                        containedResourceJson={exposure.eligibility.reference && containedResourceDictionary[exposure.eligibility.reference]} /><br /></div>}
                                                    {Array.isArray(exposure.intendedExposure) &&
                                                        exposure.intendedExposure.map((intended, intendedIndex) => {
                                                            return <div key={intendedIndex}><b>Exposure: </b><DisplayFromFHIRReference reference={intended}
                                                                containedResourceJson={intended.reference && containedResourceDictionary[intended.reference]} /></div>
                                                        })}
                                                    {exposure.observedGroup && <span><b>Observed Group: </b><DisplayFromFHIRReference reference={exposure.observedGroup}
                                                        containedResourceJson={exposure.observedGroup.reference && containedResourceDictionary[exposure.observedGroup.reference]} /><br /></span>}
                                                </div>
                                            </div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    <ArrayEntry datatype='ResearchStudyObjective'
                                        containedResourceDictionary={containedResourceDictionary}
                                        elementName='objective' fieldLabel='Objective'
                                        startCollapsed
                                        startingValue={researchStudyResourceState.objective}
                                        setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.objective) &&
                                        researchStudyResourceState.objective.map((objective, objectiveIndex) => {
                                            return <div key={objectiveIndex}><b>Objective: </b>
                                                <div style={{ marginLeft: "24px" }}>
                                                    {objective.name && <p><b>Name: </b>{objective.name}</p>}
                                                    {objective.type && <span><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={objective.type} /><br /></span>}
                                                    {objective.description && <div><b>Description: </b><DisplayFromFHIRMarkdown markdown={objective.description} /><br /><br /></div>}

                                                    {Array.isArray(objective.outcomeMeasure) &&
                                                        <>
                                                            <OutcomeMeasuresTable outcomeMeasure={objective.outcomeMeasure}
                                                                containedResourceDictionary={containedResourceDictionary} />
                                                            {/*objective.outcomeMeasure.map((outcomeMeasure, outcomeMeasureIndex) => {
                                                            let name = outcomeMeasure.name || "";
                                                            let type = outcomeMeasure.type || "";
                                                            let description = outcomeMeasure.description || "";
                                                            let endpoint = outcomeMeasure.endpoint || "";
                                                            let population = outcomeMeasure.population || "";
                                                            let intervention = outcomeMeasure.intervention || "";
                                                            let comparator = outcomeMeasure.comparator || "";
                                                            let summaryMeasure = outcomeMeasure.summaryMeasure || "";
                                                            let eventHandling = outcomeMeasure.eventHandling || [];
                                                            return <div key={outcomeMeasureIndex} >
                                                                <h4>Outcome Measure {outcomeMeasureIndex + 1}</h4>
                                                                <div style={{ marginLeft: "24px" }}>
                                                                    {name && <p><b>Name: </b>{name}</p>}
                                                                    {type && <span><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={type} /><br /></span>}
                                                                    {description && <span><b>Description: </b><DisplayFromFHIRMarkdown markdown={description} /><br /></span>}
                                                                    {endpoint && <span><b>Endpoint: </b><DisplayFromFHIRReference reference={endpoint} /><br /></span>}
                                                                    {population && <span><b>Population: </b><DisplayFromFHIRReference reference={population} /><br /></span>}
                                                                    {intervention && <span><b>Intervention Group: </b><DisplayFromFHIRReference reference={intervention} /><br /></span>}
                                                                    {comparator && <span><b>Comparator Group: </b><DisplayFromFHIRReference reference={comparator} /><br /></span>}
                                                                    {summaryMeasure && <span><b>Summary measure: </b><DisplayFromFHIRCodeableConcept codeableConcept={summaryMeasure} /><br /></span>}
                                                                    {Array.isArray(eventHandling) &&
                                                                        eventHandling.map((ice, iceIndex) => {
                                                                            let event = ice.event || "";
                                                                            let group = ice.group || "";
                                                                            let handling = ice.handling || "";
                                                                            let description = ice.description || "";
                                                                            return <div key={iceIndex}>
                                                                                <h4>Handling of Intercurrent Event {ice.id || (iceIndex + 1)}</h4>
                                                                                <div style={{ marginLeft: "24px" }}>
                                                                                    {event && <span><b>Event: </b><DisplayFromFHIRCodeableConcept codeableConcept={event} /><br /></span>}
                                                                                    {group && <span><b>Group: </b><DisplayFromFHIRCodeableConcept codeableConcept={group} /><br /></span>}
                                                                                    {handling && <span><b>Handling: </b><DisplayFromFHIRCodeableConcept codeableConcept={handling} /><br /></span>}
                                                                                    {description && <span><b>Description: </b><DisplayFromFHIRMarkdown markdown={description} /><br /></span>}
                                                                                </div>
                                                                            </div>
                                                                        })}
                                                                </div>
                                                            </div>
                                                        })*/}
                                                        </>}
                                                    {Array.isArray(objective.extension) &&
                                                        objective.extension.map((extension, extensionIndex) => {
                                                            if (extension.url === "http://hl7.org/fhir/uv/ebm/StructureDefinition/research-study-estimand") {
                                                                let label;
                                                                let type
                                                                let endpoint;
                                                                let population;
                                                                let intervention;
                                                                let comparator;
                                                                let summaryMeasure;
                                                                let eventHandling = [];
                                                                for (const ext of extension.extension) {
                                                                    if (ext.url === "label") {
                                                                        label = ext.valueString;
                                                                    } else if (ext.url === "type") {
                                                                        type = ext.valueCodeableConcept;
                                                                    } else if (ext.url === "endpoint") {
                                                                        endpoint = ext.valueReference;
                                                                    } else if (ext.url === "population") {
                                                                        population = ext.valueReference;
                                                                    } else if (ext.url === "interventionGroup" || ext.url === "intervention") {
                                                                        intervention = ext.valueId;
                                                                    } else if (ext.url === "comparatorGroup" || ext.url === "comparator") {
                                                                        comparator = ext.valueId;
                                                                    } else if (ext.url === "summaryMeasure") {
                                                                        summaryMeasure = ext.valueCodeableConcept;
                                                                    } else if (ext.url === "eventHandling") {
                                                                        eventHandling.push(ext);
                                                                    }
                                                                }
                                                                return <div key={extensionIndex} >
                                                                    <h4>Estimand {extensionIndex + 1}</h4>
                                                                    <div style={{ marginLeft: "24px" }}>
                                                                        {label && <p><b>Label: </b>{label}</p>}
                                                                        {type && <span><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={type} /><br /></span>}
                                                                        {endpoint && <span><b>Endpoint: </b><DisplayFromFHIRReference reference={endpoint}
                                                                            containedResourceJson={endpoint.reference && containedResourceDictionary[endpoint.reference]} /><br /></span>}
                                                                        {population && <span><b>Population: </b><DisplayFromFHIRReference reference={population}
                                                                            containedResourceJson={population.reference && containedResourceDictionary[population.reference]} /><br /></span>}
                                                                        {intervention && <p><b>Intervention group: </b>Comparison group {intervention}</p>}
                                                                        {comparator && <p><b>Comparator group: </b>Comparison group {comparator}</p>}
                                                                        {summaryMeasure && <span><b>Summary measure: </b><DisplayFromFHIRCodeableConcept codeableConcept={summaryMeasure} /><br /></span>}
                                                                        {Array.isArray(eventHandling) &&
                                                                            eventHandling.map((ice, iceIndex) => {
                                                                                let event;
                                                                                let group;
                                                                                let handling;
                                                                                let description;
                                                                                for (const extension of ice.extension) {
                                                                                    if (extension.url === "event") {
                                                                                        event = extension.valueCodeableConcept;
                                                                                    } else if (extension.url === "group") {
                                                                                        group = extension.valueCodeableConcept;
                                                                                    } else if (extension.url === "handling") {
                                                                                        handling = extension.valueCodeableConcept;
                                                                                    } else if (extension.url === "description") {
                                                                                        description = extension.valueMarkdown;
                                                                                    }
                                                                                }
                                                                                return <div key={iceIndex}>
                                                                                    <h4>Handling of Intercurrent Event {ice.id || (iceIndex + 1)}</h4>
                                                                                    <div style={{ marginLeft: "24px" }}>
                                                                                        {event && <span><b>Event: </b><DisplayFromFHIRCodeableConcept codeableConcept={event} /><br /></span>}
                                                                                        {group && <span><b>Group: </b><DisplayFromFHIRCodeableConcept codeableConcept={group} /><br /></span>}
                                                                                        {handling && <span><b>Handling: </b><DisplayFromFHIRCodeableConcept codeableConcept={handling} /><br /></span>}
                                                                                        {description && <span><b>Description: </b><DisplayFromFHIRMarkdown markdown={description} /><br /></span>}
                                                                                    </div>
                                                                                </div>
                                                                            })}
                                                                    </div>
                                                                </div>
                                                            } else {
                                                                return <></>;
                                                            }
                                                        })}
                                                </div>
                                            </div>
                                        })}
                                </>
                        }
                        {
                            editMode ?
                                <>
                                    {Array.isArray(researchStudyResourceState.outcomeMeasure) &&
                                        <ArrayEntry datatype='ResearchStudyOutcomeMeasure'
                                            containedResourceDictionary={containedResourceDictionary}
                                            elementName='outcomeMeasure' fieldLabel='Outcome Measure (pre-2025 FHIR)'
                                            startCollapsed startEmptyArrayClosed={true}
                                            startingValue={researchStudyResourceState.outcomeMeasure} setResourceState={setResearchStudyResourceState} />
                                    }
                                </>
                                :
                                <>
                                    {Array.isArray(researchStudyResourceState.outcomeMeasure) &&
                                        researchStudyResourceState.outcomeMeasure.map((outcome, outcomeIndex) => {
                                            return <div key={outcomeIndex}><b>Outcome Measure: </b>
                                                <div style={{ marginLeft: "24px" }}>
                                                    {outcome.name && <p><b>Name: </b>{outcome.name}</p>}
                                                    {Array.isArray(outcome.type) &&
                                                        outcome.type.map((type, typeIndex) => {
                                                            return <div key={typeIndex}><b>Type: </b><DisplayFromFHIRCodeableConcept codeableConcept={type} /></div>
                                                        })}
                                                    {outcome.description && <div><b>Description: </b><DisplayFromFHIRMarkdown markdown={outcome.description} /><br /><br /></div>}
                                                    {outcome.reference && <span><b>Outcome Definition: </b><DisplayFromFHIRReference reference={outcome.reference}
                                                        containedResourceJson={outcome.reference.reference && containedResourceDictionary[outcome.reference.reference]} /><br /></span>}
                                                </div>
                                            </div>
                                        })}
                                </>
                        }
                    </div>
                    <h3 id="results">Results</h3>
                    <div style={{ marginLeft: "24px" }}>
                        {
                            editMode ?
                                <>
                                    {isSystematicReviewProfile && <>
                                        <UnsignedIntEntry
                                            elementName='numberOfStudiesIdentified'
                                            fieldLabel='Number of studies identified'
                                            startingValue={extensionState.numberOfStudiesIdentified}
                                            setResourceState={setExtensionState} />
                                        <UnsignedIntEntry
                                            elementName='numberOfStudiesIncluded'
                                            fieldLabel='Number of studies included'
                                            startingValue={extensionState.numberOfStudiesIncluded}
                                            setResourceState={setExtensionState} />
                                        <ArrayEntry datatype='Reference'
                                            elementName='includedStudy'
                                            fieldLabel='Included study'
                                            referencedResourceTypes={['Citation', 'Composition', 'ResearchStudy']}
                                            enableCreation={true} startCollapsed
                                            startEmptyArrayClosed={true}
                                            startingValue={extensionState.includedStudy}
                                            setResourceState={setExtensionState} />
                                        <ArrayEntry datatype='Reference'
                                            elementName='excludedStudy'
                                            fieldLabel='Excluded study'
                                            referencedResourceTypes={['Citation', 'Composition', 'ResearchStudy']}
                                            enableCreation={true} startCollapsed
                                            startEmptyArrayClosed={true}
                                            startingValue={extensionState.excludedStudy}
                                            setResourceState={setExtensionState} />
                                    </>}
                                    <ArrayEntry datatype='Reference' elementName='result'
                                        fieldLabel='Result (as Reference)'
                                        startCollapsed startEmptyArrayClosed={true}
                                        referencedResourceTypes={resultReferenceResourceTypes} enableCreation={true}
                                        startingValue={researchStudyResourceState.result || null} setResourceState={setResearchStudyResourceState} />
                                </>
                                :
                                <>
                                    {
                                        (Array.isArray(researchStudyResourceState.result) || !isNaN(extensionState.numberOfStudiesIdentified) ||
                                            !isNaN(extensionState.numberOfStudiesIncluded) || extensionState.includedStudy?.length || extensionState.excludedStudy?.length) ? <>
                                            {!isNaN(extensionState.numberOfStudiesIdentified) &&
                                                <p><b>Number of studies identified: </b>{extensionState.numberOfStudiesIdentified}</p>}
                                            {!isNaN(extensionState.numberOfStudiesIncluded) &&
                                                <p><b>Number of studies included: </b>{extensionState.numberOfStudiesIncluded}</p>}
                                            {Array.isArray(extensionState.includedStudy) &&
                                                extensionState.includedStudy.map((result, resultIndex) => {
                                                    return <div key={resultIndex}><b>Included study: </b><DisplayFromFHIRReference reference={result}
                                                        containedResourceJson={result.reference && containedResourceDictionary[result.reference]} /></div>
                                                })}
                                            {Array.isArray(extensionState.excludedStudy) &&
                                                extensionState.excludedStudy.map((result, resultIndex) => {
                                                    return <div key={resultIndex}><b>Excluded study: </b><DisplayFromFHIRReference reference={result}
                                                        containedResourceJson={result.reference && containedResourceDictionary[result.reference]} /></div>
                                                })}
                                            {Array.isArray(researchStudyResourceState.result) &&
                                                researchStudyResourceState.result.map((result, resultIndex) => {
                                                    return <div key={resultIndex}><b>Result: </b><DisplayFromFHIRReference reference={result}
                                                        containedResourceJson={result.reference && containedResourceDictionary[result.reference]} /></div>
                                                })}
                                        </>
                                            :
                                            <p>No results</p>
                                    }
                                </>
                        }
                    </div>
                </div>
            </div>
        </>}
    </div>
}

export default ResearchStudyDisplay;
export {
    ResearchStudyAssociatedPartyEntry, ResearchStudyComparisonGroupEntry, ResearchStudyLabelEntry,
    EventHandlingEntry, StudyAmendmentEntry, ResearchStudyObjectiveEntry,
    ResearchStudyOutcomeMeasureEntry, ResearchStudyProgressStatusEntry,
    ResearchStudyRecruitmentEntry
};